import React, { useState, useEffect, useMemo } from "react";
import axiosInstance from "../../services/axiosInstance";
import * as XLSX from "xlsx";
import "./adminSchoolCompetition.css";
import PastCompetitionVideosSection from "./PastCompetitionVideosSection";
import PrizeCeremonyVideosSection from "./PrizeCeremonyVideosSection";
import AsyncVideoPlayer from "../../components/AsyncVideoPlayer";

export default function AdminCompetitionManager() {
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedSubmissions, setExpandedSubmissions] = useState({}); // Tracks which submission's evaluation details are expanded
  const [marksSortOrder, setMarksSortOrder] = useState("NONE"); // Tracks sorting for Judge Marks: "NONE", "DESC", "ASC"

  const [masterSchools, setMasterSchools] = useState([]);
  const [showSchoolModal, setShowSchoolModal] = useState(false);
  const [editingSchool, setEditingSchool] = useState(null);
  const [masterSchoolInput, setMasterSchoolInput] = useState({
    name: "",
    address: "",
    code: "",
  });
  const [masterSchoolInputMode, setMasterSchoolInputMode] = useState("MANUAL"); // MANUAL vs FILE

  useEffect(() => {
    fetchCompetitions();
    fetchSubmissions();
    fetchMasterSchools();
    fetchPrizeVideos();
  }, []);

  const normalizeCategory = (cat) => {
    if (!cat) return cat;
    const trimmed = String(cat).trim();
    if (/^kojo(\s+competition)?$/i.test(trimmed)) {
      return "KoJo Competition";
    }
    if (/^science(\s+compertition|\s+competition)?$/i.test(trimmed)) {
      return "Science Competition";
    }
    if (/^public\s+speaking$/i.test(trimmed)) {
      return "Public Speaking";
    }
    return trimmed;
  };

  useEffect(() => {
    if (competitions.length > 0) {
      const uniqueCats = Array.from(new Set(competitions.map(c => c.category).filter(Boolean).map(normalizeCategory)));
      setCategories(prev => {
        const merged = Array.from(new Set([...prev.map(normalizeCategory), ...uniqueCats]));
        return merged;
      });
    }
  }, [competitions]);

  const fetchMasterSchools = async () => {
    try {
      const res = await axiosInstance.get("/admin/school-competitions/schools");
      if (res.data && Array.isArray(res.data)) {
        setMasterSchools(res.data);
      }
    } catch (err) {
      console.error("Failed to fetch master schools", err);
    }
  };

  const handleAddMasterSchoolSingle = async (e) => {
    e.preventDefault();
    if (!masterSchoolInput.name.trim()) return;
    try {
      if (editingSchool) {
        const res = await axiosInstance.put(
          `/admin/school-competitions/schools/${editingSchool.id}`,
          masterSchoolInput,
        );
        setMasterSchools(
          masterSchools.map((s) => (s.id === editingSchool.id ? res.data : s)),
        );
        setMsg(`✓ School '${res.data.name}' updated successfully!`);
      } else {
        const res = await axiosInstance.post(
          "/admin/school-competitions/schools",
          masterSchoolInput,
        );
        setMasterSchools([...masterSchools, res.data]);
        setMsg(`✓ School '${res.data.name}' added successfully!`);
      }
      setMasterSchoolInput({ name: "", address: "", code: "" });
      setEditingSchool(null);
    } catch (err) {
      alert(err.response?.data || "Failed to save school");
    }
  };

  const handleDeleteMasterSchool = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete school '${name}'?`))
      return;
    try {
      await axiosInstance.delete(`/admin/school-competitions/schools/${id}`);
      setMasterSchools(masterSchools.filter((s) => s.id !== id));
      setMsg(`✓ School '${name}' deleted successfully!`);
    } catch (err) {
      alert("Failed to delete school.");
    }
  };

  const [selectedBulkFile, setSelectedBulkFile] = useState(null);

  const handleMasterFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedBulkFile(file);
    }
  };

  const handleProcessMasterFileUpload = () => {
    if (!selectedBulkFile) {
      alert("Please choose a file first!");
      return;
    }
    const file = selectedBulkFile;
    setUploadedFileName(file.name);

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        let parsedSchools = [];
        const buffer = evt.target.result;

        if (
          file.name.endsWith(".xlsx") ||
          file.name.endsWith(".xls") ||
          file.name.endsWith(".csv")
        ) {
          const workbook = XLSX.read(buffer, {
            type: file.name.endsWith(".csv") ? "string" : "array",
          });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const rawData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          rawData.forEach((row) => {
            if (Array.isArray(row)) {
              row.forEach((cell) => {
                if (cell !== undefined && cell !== null) {
                  const cleaned = String(cell).trim();
                  if (
                    cleaned &&
                    cleaned.length >= 3 &&
                    !/^\d+$/.test(cleaned) &&
                    !/^\+?\d[\d\s-]{8,}$/.test(cleaned) &&
                    !/^s\.?no$/i.test(cleaned) &&
                    !/^sr\.?no$/i.test(cleaned) &&
                    !/^sl\.?no$/i.test(cleaned) &&
                    !/^school\s*name$/i.test(cleaned) &&
                    !/^schools\s*list$/i.test(cleaned) &&
                    !/^address$/i.test(cleaned) &&
                    !/^contact\s*number$/i.test(cleaned) &&
                    !/^phone\s*number$/i.test(cleaned)
                  ) {
                    parsedSchools.push(cleaned);
                  }
                }
              });
            }
          });
        } else {
          const lines = String(buffer)
            .split(/\r\n|\n/)
            .map((l) => l.trim())
            .filter((l) => l.length > 2);
          parsedSchools = lines;
        }

        const uniqueSchools = Array.from(
          new Set(parsedSchools.map((s) => s.trim())),
        ).filter(Boolean);

        if (uniqueSchools.length > 0) {
          const res = await axiosInstance.post(
            "/admin/school-competitions/schools/bulk",
            uniqueSchools,
          );
          fetchMasterSchools();
          setMsg(
            `✓ ${uniqueSchools.length} school names added to Central Schools List!`,
          );
          setSelectedBulkFile(null);
        } else {
          alert("No valid school names found in file.");
        }
      } catch (err) {
        console.error("Error reading school file", err);
        alert("Error parsing file.");
      }
    };

    if (file.name.endsWith(".csv")) {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  };

  const fetchCompetitions = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/admin/school-competitions");
      if (res.data && Array.isArray(res.data)) {
        setCompetitions(res.data);
      }
    } catch (err) {
      console.error("Failed to fetch admin competitions", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubmissions = async () => {
    try {
      const res = await axiosInstance.get(
        "/admin/school-competitions/submissions",
      );
      if (res.data && Array.isArray(res.data)) {
        setSubmissions(res.data);
      }
    } catch (err) {
      console.error("Failed to fetch submissions", err);
    }
  };

  const handleRenameCategory = async (oldName, newName) => {
    if (!newName.trim()) {
      alert("Category name cannot be blank.");
      return;
    }
    try {
      await axiosInstance.put(`/admin/school-competitions/categories/rename?oldName=${encodeURIComponent(oldName)}&newName=${encodeURIComponent(newName.trim())}`);
      setCategories(prev => prev.map(c => c === oldName ? newName.trim() : c));
      setEditingCategoryIndex(null);
      setEditingCategoryValue("");
      fetchCompetitions();
      setMsg(`Category "${oldName}" renamed to "${newName}" successfully!`);
    } catch (err) {
      console.error(err);
      alert("Failed to rename category.");
    }
  };

  const handleDeleteCategory = async (name) => {
    if (!window.confirm(`Are you sure you want to delete category "${name}"? All active competitions using this category will be reset to "General".`)) {
      return;
    }
    try {
      await axiosInstance.delete(`/admin/school-competitions/categories/delete?name=${encodeURIComponent(name)}`);
      setCategories(prev => prev.filter(c => c !== name));
      fetchCompetitions();
      setMsg(`Category "${name}" deleted successfully!`);
    } catch (err) {
      console.error(err);
      alert("Failed to delete category.");
    }
  };

  const [showModal, setShowModal] = useState(false);
  const [schoolInputMode, setSchoolInputMode] = useState("MANUAL"); // MANUAL vs FILE
  const [schoolNameInput, setSchoolNameInput] = useState("");
  const [schoolList, setSchoolList] = useState([]);
  const [uploadedFileName, setUploadedFileName] = useState("");

  const [newComp, setNewComp] = useState({
    competitionId: "",
    title: "",
    description: "",
    category: "Public Speaking",
    startDate: new Date().toISOString().split("T")[0],
    endDate: "2026-08-31",
    verificationCode: "",
    winnerAnnouncementMode: "MANUAL",
  });

  const [categories, setCategories] = useState(["Public Speaking", "Science Competition", "KoJo Competition"]);
  const [isCreatingCustomCategory, setIsCreatingCustomCategory] = useState(false);
  const [customCategoryName, setCustomCategoryName] = useState("");
  const [isCreatingPrizeCustomCategory, setIsCreatingPrizeCustomCategory] = useState(false);
  const [prizeCustomCategoryName, setPrizeCustomCategoryName] = useState("");
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [newCategoryModalInput, setNewCategoryModalInput] = useState("");

  const [isManageCategoriesModalOpen, setIsManageCategoriesModalOpen] = useState(false);
  const [editingCategoryIndex, setEditingCategoryIndex] = useState(null);
  const [editingCategoryValue, setEditingCategoryValue] = useState("");

  const [msg, setMsg] = useState("");
  const [deleteTargetComp, setDeleteTargetComp] = useState(null);
  const [deleteWarningMsg, setDeleteWarningMsg] = useState("");

  const handleAddSchoolName = () => {
    if (!schoolNameInput.trim()) return;
    if (!schoolList.includes(schoolNameInput.trim())) {
      setSchoolList([...schoolList, schoolNameInput.trim()]);
    }
    setSchoolNameInput("");
  };

  const handleRemoveSchool = (name) => {
    setSchoolList(schoolList.filter((s) => s !== name));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadedFileName(file.name);

    const reader = new FileReader();

    reader.onload = (evt) => {
      try {
        let parsedSchools = [];
        const buffer = evt.target.result;

        if (
          file.name.endsWith(".xlsx") ||
          file.name.endsWith(".xls") ||
          file.name.endsWith(".csv")
        ) {
          const workbook = XLSX.read(buffer, {
            type: file.name.endsWith(".csv") ? "string" : "array",
          });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const rawData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          rawData.forEach((row) => {
            if (Array.isArray(row)) {
              row.forEach((cell) => {
                if (cell !== undefined && cell !== null) {
                  const cleaned = String(cell).trim();
                  // Check if cell is a valid school name (not pure numbers, not header titles)
                  if (
                    cleaned &&
                    cleaned.length >= 3 &&
                    !/^\d+$/.test(cleaned) && // Exclude serial numbers like 1, 2, 10, 11
                    !/^\+?\d[\d\s-]{8,}$/.test(cleaned) && // Exclude phone numbers
                    !/^s\.?no$/i.test(cleaned) &&
                    !/^sr\.?no$/i.test(cleaned) &&
                    !/^sl\.?no$/i.test(cleaned) &&
                    !/^school\s*name$/i.test(cleaned) &&
                    !/^schools\s*list$/i.test(cleaned) &&
                    !/^address$/i.test(cleaned) &&
                    !/^contact\s*number$/i.test(cleaned) &&
                    !/^phone\s*number$/i.test(cleaned)
                  ) {
                    parsedSchools.push(cleaned);
                  }
                }
              });
            }
          });
        } else {
          const lines = String(buffer)
            .split(/\r\n|\n/)
            .map((l) => l.trim())
            .filter((l) => l.length > 2);
          parsedSchools = lines;
        }

        const uniqueSchools = Array.from(
          new Set(parsedSchools.map((s) => s.trim())),
        ).filter(Boolean);

        if (uniqueSchools.length > 0) {
          setSchoolList((prev) =>
            Array.from(new Set([...prev, ...uniqueSchools])),
          );
          setMsg(
            `✓ ${uniqueSchools.length} school names extracted from '${file.name}'!`,
          );
        } else {
          alert("No school names found in the file.");
        }
      } catch (err) {
        console.error("Error reading school file", err);
        alert("Error parsing file.");
      }
    };

    if (file.name.endsWith(".csv")) {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  };

  const handleCreateCompetition = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...newComp,
        participatingSchools: schoolList,
        isLive: true,
        status: "LIVE",
      };
      const res = await axiosInstance.post(
        "/admin/school-competitions",
        payload,
      );
      setCompetitions((prev) => [...prev, res.data]);
      setMsg(`Competition '${newComp.title}' created successfully!`);
      setShowModal(false);
      setSchoolList([]);
      setUploadedFileName("");
      setIsCreatingCustomCategory(false);
      setCustomCategoryName("");
      setNewComp({
        competitionId: "",
        title: "",
        description: "",
        category: "Public Speaking",
        verificationCode: "",
        winnerAnnouncementMode: "MANUAL",
      });
    } catch (err) {
      console.error("Create Competition Error:", err);
      const serverErr =
        err.response?.data?.message ||
        err.response?.data ||
        err.message ||
        "Failed to create competition on server.";
      alert(`⚠️ Create Competition Error: ${serverErr}`);
    }
  };

  const toggleLiveStatus = async (comp) => {
    try {
      const res = await axiosInstance.put(
        `/admin/school-competitions/${comp.competitionId}/toggle-live`,
      );
      setCompetitions(
        competitions.map((c) =>
          c.competitionId === comp.competitionId ? res.data : c,
        ),
      );
    } catch (err) {
      console.error(err);
    }
  };

  const toggleWinnerMode = async (comp) => {
    try {
      const res = await axiosInstance.put(
        `/admin/school-competitions/${comp.competitionId}/toggle-winner-mode`,
      );
      setCompetitions(
        competitions.map((c) =>
          c.competitionId === comp.competitionId ? res.data : c,
        ),
      );
    } catch (err) {
      console.error(err);
    }
  };

  const [editingComp, setEditingComp] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isEditingCustomCategory, setIsEditingCustomCategory] = useState(false);
  const [editCustomCategoryName, setEditCustomCategoryName] = useState("");

  const toDateInputValue = (val) => {
    if (!val) return "";
    const str = String(val);
    return str.length >= 10 ? str.slice(0, 10) : str;
  };

  const COMPETITION_CATEGORIES = [
    "Public Speaking",
    "Science Competition",
    "KoJo Competition",
  ];

  const handleOpenEdit = (comp) => {
    setEditingComp({
      competitionId: comp.competitionId,
      title: comp.title || "",
      description: comp.description || "",
      category: comp.category || "Public Speaking",
      startDate:
        toDateInputValue(comp.startDate) ||
        new Date().toISOString().split("T")[0],
      endDate: toDateInputValue(comp.endDate) || "2026-08-31",
      verificationCode: comp.verificationCode || "",
      winnerAnnouncementMode: comp.winnerAnnouncementMode || "AUTOMATIC",
      participatingSchools: comp.participatingSchools || [],
    });
    setSchoolList(comp.participatingSchools || []);
    setShowEditModal(true);
  };

  const handleUpdateCompetition = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...editingComp,
        participatingSchools: schoolList,
      };
      const res = await axiosInstance.put(
        `/admin/school-competitions/${editingComp.competitionId}`,
        payload,
      );
      setCompetitions(
        competitions.map((c) =>
          c.competitionId === editingComp.competitionId ? res.data : c,
        ),
      );
      setMsg(`Competition '${editingComp.title}' updated successfully!`);
      setShowEditModal(false);
      setEditingComp(null);
      setSchoolList([]);
      setIsEditingCustomCategory(false);
      setEditCustomCategoryName("");
    } catch (err) {
      console.error(err);
      alert("Failed to update competition.");
    }
  };

  const handleExportCSV = (filteredSubs) => {
    if (!filteredSubs || filteredSubs.length === 0) {
      alert("No data available to export.");
      return;
    }

    const headers = [
      "Submission ID",
      "Competition ID",
      "Student Name",
      "Class & Roll No",
      "Group / Category",
      "School Name",
      "Submitted By",
      "Entry Title",
      "Entry Description",
      "Submission Link / Code Content",
      "Status",
      "Rejection Reason / Rejected By",
      "Winner Rank",
      "Average Score",
      "Judge 1 ID",
      "Judge 1 Score",
      "Judge 1 Remarks",
      "Judge 2 ID",
      "Judge 2 Score",
      "Judge 2 Remarks",
      "Judge 3 ID",
      "Judge 3 Score",
      "Judge 3 Remarks"
    ];

    const rows = filteredSubs.map((s) => {
      const clean = (val) => {
        if (val === null || val === undefined) return "";
        const str = String(val).replace(/"/g, '""');
        return `"${str}"`;
      };

      const classRoll = `Class ${s.classGrade || ""}, Roll ${s.rollNumber || ""}`;
      const rejection = s.status === "REJECTED" ? `${s.rejectionReason || ""} (Rejected By: ${s.rejectedBy || ""})` : "";
      const winnerRank = s.winnerRank ? `${s.winnerRank}` : "—";
      const judgeMarks = s.totalScore !== null && s.totalScore !== undefined ? `${s.totalScore}` : "—";

      const evaluations = s.evaluations || [];
      let ev1 = evaluations[0] || {};
      const ev2 = evaluations[1] || {};
      const ev3 = evaluations[2] || {};

      if (evaluations.length === 0 && s.totalScore !== null && s.totalScore !== undefined) {
        ev1 = {
          judgeId: "Legacy Judge",
          totalScore: s.totalScore,
          remarks: s.judgeRemarks || ""
        };
      }

      const judge1Id = ev1.judgeId || "—";
      const judge1Score = ev1.totalScore !== undefined ? `${ev1.totalScore}` : "—";
      const judge1Remarks = ev1.remarks || "—";

      const judge2Id = ev2.judgeId || "—";
      const judge2Score = ev2.totalScore !== undefined ? `${ev2.totalScore}` : "—";
      const judge2Remarks = ev2.remarks || "—";

      const judge3Id = ev3.judgeId || "—";
      const judge3Score = ev3.totalScore !== undefined ? `${ev3.totalScore}` : "—";
      const judge3Remarks = ev3.remarks || "—";

      return [
        clean(s.submissionId),
        clean(s.competitionId),
        clean(s.studentName),
        clean(classRoll),
        clean(s.groupCategory),
        clean(s.schoolName),
        clean(s.submittedBy),
        clean(s.entryTitle),
        clean(s.entryDescription),
        clean(s.videoUrl),
        clean(s.status),
        clean(rejection),
        clean(winnerRank),
        clean(judgeMarks),
        clean(judge1Id),
        clean(judge1Score),
        clean(judge1Remarks),
        clean(judge2Id),
        clean(judge2Score),
        clean(judge2Remarks),
        clean(judge3Id),
        clean(judge3Score),
        clean(judge3Remarks)
      ].join(",");
    });

    const csvData = [headers.join(","), ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csvData], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    
    const filterName = selectedCompetitionFilter === "ALL" ? "all_competitions" : selectedCompetitionFilter;
    const dateStr = new Date().toISOString().split("T")[0];
    link.setAttribute("download", `school_competition_submissions_${filterName}_${dateStr}.csv`);
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDeleteCompetition = (comp) => {
    setDeleteTargetComp(comp);
  };

 
  
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingSubmissionId, setRejectingSubmissionId] = useState("");
  const [rejectionReasonInput, setRejectionReasonInput] = useState("");

  const handleOpenRejectModal = (submissionId) => {
    setRejectingSubmissionId(submissionId);
    setRejectionReasonInput("");
    setShowRejectModal(true);
  };

  const handleConfirmRejectSubmission = async (e) => {
    e.preventDefault();
    if (!rejectionReasonInput.trim()) {
      alert("⚠️ Rejection reason is mandatory!");
      return;
    }
    try {
      const res = await axiosInstance.post(
        `/admin/school-competitions/submissions/${rejectingSubmissionId}/reject`,
        { reason: rejectionReasonInput.trim() },
      );
      setSubmissions(
        submissions.map((s) =>
          s.submissionId === rejectingSubmissionId ? res.data : s,
        ),
      );
      setMsg(`✓ Submission '${rejectingSubmissionId}' rejected successfully!`);
      setShowRejectModal(false);
      setRejectingSubmissionId("");
      setRejectionReasonInput("");
    } catch (err) {
      console.error(err);
      alert(err.response?.data || "Failed to reject submission.");
    }
  };

  const handleAnnounceWinner = async (submissionId, rank) => {
    const rankInt = rank ? parseInt(rank, 10) : null;
    try {
      const res = await axiosInstance.post(
        `/admin/school-competitions/submissions/${submissionId}/announce-winner`,
        { winnerRank: rankInt },
      );
      setSubmissions(
        submissions.map((s) =>
          s.submissionId === submissionId ? res.data : s,
        ),
      );
      if (rankInt) {
        const labels = {
          1: "🥇 1st Place Winner",
          2: "🥈 2nd Place Winner",
          3: "🥉 3rd Place Winner",
        };
        setMsg(
          `✓ Submission '${submissionId}' announced as ${labels[rankInt]}!`,
        );
      } else {
        setMsg(`Winner rank cleared for submission '${submissionId}'.`);
      }
    } catch (err) {
      console.error(err);
      const errorMsg =
        typeof err.response?.data === "string"
          ? err.response.data
          : err.response?.data?.message || "Failed to update winner rank.";
      alert(errorMsg);
    }
  };

  const handleOpenCreate = () => {
    setSchoolList([]);
    setUploadedFileName("");
    setSchoolNameInput("");
    setNewComp({
      competitionId: "",
      title: "",
      description: "",
      category: "Public Speaking",
      startDate: new Date().toISOString().split("T")[0],
      endDate: "2026-08-31",
      verificationCode: "",
      winnerAnnouncementMode: "MANUAL",
    });
    setShowModal(true);
  };

  const handleCloseCreate = () => {
    setShowModal(false);
    setSchoolList([]);
    setUploadedFileName("");
    setSchoolNameInput("");
    setIsCreatingCustomCategory(false);
    setCustomCategoryName("");
  };

  const [activeTab, setActiveTab] = useState("active");
  const [pastCurrentPage, setPastCurrentPage] = useState(1);
  const pastItemsPerPage = 6;
  const [submissions, setSubmissions] = useState([]);
  const [selectedCompetitionFilter, setSelectedCompetitionFilter] =
    useState("NONE");
  const [selectedGroupFilter, setSelectedGroupFilter] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [subPage, setSubPage] = useState(1);
  const subPageSize = 10;
  const [playingVideoUrl, setPlayingVideoUrl] = useState(null);
  const [downloadingCompetitionId, setDownloadingCompetitionId] = useState("");

  const handleDeleteSubmission = async (subId) => {
    if (
      window.confirm(`Are you sure you want to delete submission '${subId}'?`)
    ) {
      try {
        await axiosInstance.delete(`/admin/school-competitions/submissions/${encodeURIComponent(subId)}`);
        setSubmissions((prev) => prev.filter((s) => s.submissionId !== subId));
        setMsg(`✓ Submission '${subId}' deleted successfully!`);
        await fetchSubmissions();
        await fetchPrizeVideos();
      } catch (err) {
        console.error("Failed to delete submission", err);
        setMsg("⚠️ Failed to delete submission: " + (err.response?.data?.message || err.response?.data || err.message));
      }
    }
  };

  const getEmbedUrl = (url) => {
    if (!url) return "";
    let videoId = "";
    if (url.includes("youtu.be/")) {
      videoId = url.split("youtu.be/")[1]?.split("?")[0];
    } else if (url.includes("youtube.com/watch")) {
      const urlParams = new URLSearchParams(url.split("?")[1]);
      videoId = urlParams.get("v");
    } else if (url.includes("youtube.com/shorts/")) {
      videoId = url.split("youtube.com/shorts/")[1]?.split("?")[0];
    }
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
    }
    return url;
  };

  const [showPrizeCeremonyModal, setShowPrizeCeremonyModal] = useState(false);
  const [showPastVideoModal, setShowPastVideoModal] = useState(false);
  const [prizeVideos, setPrizeVideos] = useState([]);
  const [prizeVideosLoading, setPrizeVideosLoading] = useState(false);
  const [editingPrizeVideoId, setEditingPrizeVideoId] = useState(null);
  const [prizeVideoForm, setPrizeVideoForm] = useState({
    competitionId: "",
    competitionName: "",
    category: "",
    startDate: "",
    endDate: "",
    videoUrl: "",
  });

  const [pastVideoForm, setPastVideoForm] = useState({
    competitionName: "",
    year: new Date().getFullYear().toString(),
    month: "January",
    competitionType: "Public Speaking",
    groupCategory: "Group A",
    videoUrl: "",
    winnerRank: "1",
    prizeAmount: "",
  });

  const initialBatchWinners = [
    { groupCategory: "Group A (Class 1-2)", winnerRank: 1, label: "🥇 Group A (Class 1-2) — Rank 1 Winner", prizeAmount: "₹ 5,000", videoUrl: "", studentName: "", studentClass: "", rollNumber: "", schoolName: "" },
    { groupCategory: "Group A (Class 1-2)", winnerRank: 2, label: "🥈 Group A (Class 1-2) — Rank 2 Runner-up", prizeAmount: "₹ 3,000", videoUrl: "", studentName: "", studentClass: "", rollNumber: "", schoolName: "" },
    { groupCategory: "Group A (Class 1-2)", winnerRank: 3, label: "🥉 Group A (Class 1-2) — Rank 3 Runner-up", prizeAmount: "₹ 1,000", videoUrl: "", studentName: "", studentClass: "", rollNumber: "", schoolName: "" },
    { groupCategory: "Group A (Class 1-2)", winnerRank: 4, label: "🎁 Group A (Class 1-2) — Consolation #1", prizeAmount: "₹ 500", videoUrl: "", studentName: "", studentClass: "", rollNumber: "", schoolName: "", isConsolation: true },
    { groupCategory: "Group A (Class 1-2)", winnerRank: 5, label: "🎁 Group A (Class 1-2) — Consolation #2", prizeAmount: "₹ 500", videoUrl: "", studentName: "", studentClass: "", rollNumber: "", schoolName: "", isConsolation: true },
    { groupCategory: "Group A (Class 1-2)", winnerRank: 6, label: "🎁 Group A (Class 1-2) — Consolation #3", prizeAmount: "₹ 500", videoUrl: "", studentName: "", studentClass: "", rollNumber: "", schoolName: "", isConsolation: true },
    
    { groupCategory: "Group B (Class 3-5)", winnerRank: 1, label: "🥇 Group B (Class 3-5) — Rank 1 Winner", prizeAmount: "₹ 5,000", videoUrl: "", studentName: "", studentClass: "", rollNumber: "", schoolName: "" },
    { groupCategory: "Group B (Class 3-5)", winnerRank: 2, label: "🥈 Group B (Class 3-5) — Rank 2 Runner-up", prizeAmount: "₹ 3,000", videoUrl: "", studentName: "", studentClass: "", rollNumber: "", schoolName: "" },
    { groupCategory: "Group B (Class 3-5)", winnerRank: 3, label: "🥉 Group B (Class 3-5) — Rank 3 Runner-up", prizeAmount: "₹ 1,000", videoUrl: "", studentName: "", studentClass: "", rollNumber: "", schoolName: "" },
    { groupCategory: "Group B (Class 3-5)", winnerRank: 4, label: "🎁 Group B (Class 3-5) — Consolation #1", prizeAmount: "₹ 500", videoUrl: "", studentName: "", studentClass: "", rollNumber: "", schoolName: "", isConsolation: true },
    { groupCategory: "Group B (Class 3-5)", winnerRank: 5, label: "🎁 Group B (Class 3-5) — Consolation #2", prizeAmount: "₹ 500", videoUrl: "", studentName: "", studentClass: "", rollNumber: "", schoolName: "", isConsolation: true },
    { groupCategory: "Group B (Class 3-5)", winnerRank: 6, label: "🎁 Group B (Class 3-5) — Consolation #3", prizeAmount: "₹ 500", videoUrl: "", studentName: "", studentClass: "", rollNumber: "", schoolName: "", isConsolation: true },
    
    { groupCategory: "Group C (Class 6-8)", winnerRank: 1, label: "🥇 Group C (Class 6-8) — Rank 1 Winner", prizeAmount: "₹ 5,000", videoUrl: "", studentName: "", studentClass: "", rollNumber: "", schoolName: "" },
    { groupCategory: "Group C (Class 6-8)", winnerRank: 2, label: "🥈 Group C (Class 6-8) — Rank 2 Runner-up", prizeAmount: "₹ 3,000", videoUrl: "", studentName: "", studentClass: "", rollNumber: "", schoolName: "" },
    { groupCategory: "Group C (Class 6-8)", winnerRank: 3, label: "🥉 Group C (Class 6-8) — Rank 3 Runner-up", prizeAmount: "₹ 1,000", videoUrl: "", studentName: "", studentClass: "", rollNumber: "", schoolName: "" },
    { groupCategory: "Group C (Class 6-8)", winnerRank: 4, label: "🎁 Group C (Class 6-8) — Consolation #1", prizeAmount: "₹ 500", videoUrl: "", studentName: "", studentClass: "", rollNumber: "", schoolName: "", isConsolation: true },
    { groupCategory: "Group C (Class 6-8)", winnerRank: 5, label: "🎁 Group C (Class 6-8) — Consolation #2", prizeAmount: "₹ 500", videoUrl: "", studentName: "", studentClass: "", rollNumber: "", schoolName: "", isConsolation: true },
    { groupCategory: "Group C (Class 6-8)", winnerRank: 6, label: "🎁 Group C (Class 6-8) — Consolation #3", prizeAmount: "₹ 500", videoUrl: "", studentName: "", studentClass: "", rollNumber: "", schoolName: "", isConsolation: true },

    { groupCategory: "Group C (Class 6)", winnerRank: 1, label: "🥇 Class 6th — Rank 1 Winner", prizeAmount: "₹ 5,000", videoUrl: "", studentName: "", studentClass: "6", rollNumber: "", schoolName: "" },
    { groupCategory: "Group C (Class 6)", winnerRank: 2, label: "🥈 Class 6th — Rank 2 Runner-up", prizeAmount: "₹ 3,000", videoUrl: "", studentName: "", studentClass: "6", rollNumber: "", schoolName: "" },
    { groupCategory: "Group C (Class 6)", winnerRank: 3, label: "🥉 Class 6th — Rank 3 Runner-up", prizeAmount: "₹ 1,000", videoUrl: "", studentName: "", studentClass: "6", rollNumber: "", schoolName: "" },
    { groupCategory: "Group C (Class 6)", winnerRank: 4, label: "🎁 Class 6th — Consolation #1", prizeAmount: "₹ 500", videoUrl: "", studentName: "", studentClass: "6", rollNumber: "", schoolName: "", isConsolation: true },
    { groupCategory: "Group C (Class 6)", winnerRank: 5, label: "🎁 Class 6th — Consolation #2", prizeAmount: "₹ 500", videoUrl: "", studentName: "", studentClass: "6", rollNumber: "", schoolName: "", isConsolation: true },
    { groupCategory: "Group C (Class 6)", winnerRank: 6, label: "🎁 Class 6th — Consolation #3", prizeAmount: "₹ 500", videoUrl: "", studentName: "", studentClass: "6", rollNumber: "", schoolName: "", isConsolation: true },

    { groupCategory: "Group C (Class 7)", winnerRank: 1, label: "🥇 Class 7th — Rank 1 Winner", prizeAmount: "₹ 5,000", videoUrl: "", studentName: "", studentClass: "7", rollNumber: "", schoolName: "" },
    { groupCategory: "Group C (Class 7)", winnerRank: 2, label: "🥈 Class 7th — Rank 2 Runner-up", prizeAmount: "₹ 3,000", videoUrl: "", studentName: "", studentClass: "7", rollNumber: "", schoolName: "" },
    { groupCategory: "Group C (Class 7)", winnerRank: 3, label: "🥉 Class 7th — Rank 3 Runner-up", prizeAmount: "₹ 1,000", videoUrl: "", studentName: "", studentClass: "7", rollNumber: "", schoolName: "" },
    { groupCategory: "Group C (Class 7)", winnerRank: 4, label: "🎁 Class 7th — Consolation #1", prizeAmount: "₹ 500", videoUrl: "", studentName: "", studentClass: "7", rollNumber: "", schoolName: "", isConsolation: true },
    { groupCategory: "Group C (Class 7)", winnerRank: 5, label: "🎁 Class 7th — Consolation #2", prizeAmount: "₹ 500", videoUrl: "", studentName: "", studentClass: "7", rollNumber: "", schoolName: "", isConsolation: true },
    { groupCategory: "Group C (Class 7)", winnerRank: 6, label: "🎁 Class 7th — Consolation #3", prizeAmount: "₹ 500", videoUrl: "", studentName: "", studentClass: "7", rollNumber: "", schoolName: "", isConsolation: true },

    { groupCategory: "Group C (Class 8)", winnerRank: 1, label: "🥇 Class 8th — Rank 1 Winner", prizeAmount: "₹ 5,000", videoUrl: "", studentName: "", studentClass: "8", rollNumber: "", schoolName: "" },
    { groupCategory: "Group C (Class 8)", winnerRank: 2, label: "🥈 Class 8th — Rank 2 Runner-up", prizeAmount: "₹ 3,000", videoUrl: "", studentName: "", studentClass: "8", rollNumber: "", schoolName: "" },
    { groupCategory: "Group C (Class 8)", winnerRank: 3, label: "🥉 Class 8th — Rank 3 Runner-up", prizeAmount: "₹ 1,000", videoUrl: "", studentName: "", studentClass: "8", rollNumber: "", schoolName: "" },
    { groupCategory: "Group C (Class 8)", winnerRank: 4, label: "🎁 Class 8th — Consolation #1", prizeAmount: "₹ 500", videoUrl: "", studentName: "", studentClass: "8", rollNumber: "", schoolName: "", isConsolation: true },
    { groupCategory: "Group C (Class 8)", winnerRank: 5, label: "🎁 Class 8th — Consolation #2", prizeAmount: "₹ 500", videoUrl: "", studentName: "", studentClass: "8", rollNumber: "", schoolName: "", isConsolation: true },
    { groupCategory: "Group C (Class 8)", winnerRank: 6, label: "🎁 Class 8th — Consolation #3", prizeAmount: "₹ 500", videoUrl: "", studentName: "", studentClass: "8", rollNumber: "", schoolName: "", isConsolation: true },
    
    { groupCategory: "Group D (Class 9-12)", winnerRank: 1, label: "🥇 Group D (Class 9-12) — Rank 1 Winner", prizeAmount: "₹ 5,000", videoUrl: "", studentName: "", studentClass: "", rollNumber: "", schoolName: "" },
    { groupCategory: "Group D (Class 9-12)", winnerRank: 2, label: "🥈 Group D (Class 9-12) — Rank 2 Runner-up", prizeAmount: "₹ 3,000", videoUrl: "", studentName: "", studentClass: "", rollNumber: "", schoolName: "" },
    { groupCategory: "Group D (Class 9-12)", winnerRank: 3, label: "🥉 Group D (Class 9-12) — Rank 3 Runner-up", prizeAmount: "₹ 1,000", videoUrl: "", studentName: "", studentClass: "", rollNumber: "", schoolName: "" },
    { groupCategory: "Group D (Class 9-12)", winnerRank: 4, label: "🎁 Group D (Class 9-12) — Consolation #1", prizeAmount: "₹ 500", videoUrl: "", studentName: "", studentClass: "", rollNumber: "", schoolName: "", isConsolation: true },
    { groupCategory: "Group D (Class 9-12)", winnerRank: 5, label: "🎁 Group D (Class 9-12) — Consolation #2", prizeAmount: "₹ 500", videoUrl: "", studentName: "", studentClass: "", rollNumber: "", schoolName: "", isConsolation: true },
    { groupCategory: "Group D (Class 9-12)", winnerRank: 6, label: "🎁 Group D (Class 9-12) — Consolation #3", prizeAmount: "₹ 500", videoUrl: "", studentName: "", studentClass: "", rollNumber: "", schoolName: "", isConsolation: true },
  ];

  const [batchWinners, setBatchWinners] = useState(initialBatchWinners);
  const [includeConsolation, setIncludeConsolation] = useState({});
  const [consolationShowOnWeb, setConsolationShowOnWeb] = useState({});
  const [isEditingGroup, setIsEditingGroup] = useState(false);
  const [editingGroupCompName, setEditingGroupCompName] = useState("");
  const [editingGroupCompId, setEditingGroupCompId] = useState(null);

  const [isCreatingPastCustomCategory, setIsCreatingPastCustomCategory] = useState(false);
  const [pastCustomCategoryName, setPastCustomCategoryName] = useState("");
  const [uploadingVideo, setUploadingVideo] = useState(false);

  const [adminVideoYearFilter, setAdminVideoYearFilter] = useState("ALL");
  const [adminVideoMonthFilter, setAdminVideoMonthFilter] = useState("ALL");
  const [adminVideoCategoryFilter, setAdminVideoCategoryFilter] = useState("ALL");
  const [adminVideoGroupFilter, setAdminVideoGroupFilter] = useState("ALL");

  const [selectedCompetitionModal, setSelectedCompetitionModal] = useState(null);

  const [appliedYearFilter, setAppliedYearFilter] = useState("ALL");
  const [appliedMonthFilter, setAppliedMonthFilter] = useState("ALL");
  const [appliedCategoryFilter, setAppliedCategoryFilter] = useState("ALL");
  const [appliedGroupFilter, setAppliedGroupFilter] = useState("ALL");

  const handleSearch4thTabVideos = () => {
    setAppliedYearFilter(adminVideoYearFilter);
    setAppliedMonthFilter(adminVideoMonthFilter);
    setAppliedCategoryFilter(adminVideoCategoryFilter);
    setAppliedGroupFilter(adminVideoGroupFilter);
  };

  const handleReset4thTabVideos = () => {
    setAdminVideoYearFilter("ALL");
    setAdminVideoMonthFilter("ALL");
    setAdminVideoCategoryFilter("ALL");
    setAdminVideoGroupFilter("ALL");
    setAppliedYearFilter("ALL");
    setAppliedMonthFilter("ALL");
    setAppliedCategoryFilter("ALL");
    setAppliedGroupFilter("ALL");
  };

  const [editingWinnerItem, setEditingWinnerItem] = useState(null);

  const handleDeleteCompetitionGroup = async (compGroup, skipConfirm = false) => {
    if (!compGroup) return;
    const targetTitle = compGroup.competitionName || "this competition";
    if (!skipConfirm && !window.confirm(`⚠️ Are you sure you want to delete competition "${targetTitle}" and all associated winners and prize videos?`)) {
      return;
    }
    try {
      const videos = compGroup.videos || [];
      const compId = compGroup.competitionId;

      // 1. Reset announced student submissions if any
      const subEntries = videos.filter((v) => v.id && (String(v.id).startsWith("sub-") || String(v.id).startsWith("winner-sub-") || v.isAnnouncedWinner));
      for (const sub of subEntries) {
        const subId = String(sub.id).replace(/^(sub-|winner-sub-)/, "");
        try {
          await axiosInstance.post(`/admin/school-competitions/submissions/${encodeURIComponent(subId)}/announce-winner`, { winnerRank: 0 });
          await axiosInstance.delete(`/admin/school-competitions/prize-videos/sub-${encodeURIComponent(subId)}`);
        } catch (subErr) {
          console.warn("Failed resetting winner rank for submission", subId, subErr);
        }
      }

      // 2. Bulk delete prize distribution videos atomically by competition params
      try {
        await axiosInstance.delete("/admin/school-competitions/prize-videos/by-competition", {
          params: {
            competitionId: compId || "",
            competitionName: compGroup.competitionName || "",
          },
        });
      } catch (bulkErr) {
        console.warn("Bulk delete endpoint failed, falling back to individual ids", bulkErr);
      }

      if (compId) {
        try {
          await axiosInstance.delete(`/admin/school-competitions/prize-videos/${encodeURIComponent(compId)}`);
        } catch (e) {}
        try {
          await axiosInstance.delete(`/admin/school-competitions/ceremony-videos/${encodeURIComponent(compId)}`);
        } catch (e) {}
      }

      // 3. Fallback: delete individual non-submission video rows
      const prizeVideoEntries = videos.filter((v) => !v.isAnnouncedWinner && v.id && !String(v.id).startsWith("sub-") && !String(v.id).startsWith("winner-sub-"));
      for (const pv of prizeVideoEntries) {
        const cleanId = String(pv.id).replace(/^(prize-video-|video-)/, "");
        try {
          await axiosInstance.delete(`/admin/school-competitions/prize-videos/${encodeURIComponent(cleanId)}`);
        } catch (delErr) {}
      }

      // 4. If registered competition, soft-delete it so it is removed from active/over tabs as well
      if (compId && competitions.some((c) => c.competitionId === compId)) {
        try {
          await axiosInstance.delete(`/admin/school-competitions/${encodeURIComponent(compId)}`);
          setCompetitions((prev) => prev.filter((c) => c.competitionId !== compId));
        } catch (compErr) {
          console.warn("Failed to delete main competition entity", compErr);
        }
      }

      // Optimistically filter out deleted group immediately from prizeVideos
      setPrizeVideos((prev) =>
        prev.filter((p) => {
          if (compId && p.competitionId === compId) return false;
          if (compGroup.competitionName && p.competitionName && p.competitionName.trim().toLowerCase() === compGroup.competitionName.trim().toLowerCase()) return false;
          return true;
        })
      );

      // Optimistically clear winner ranks in submissions
      if (compId) {
        setSubmissions((prev) =>
          prev.map((s) => (s.competitionId === compId ? { ...s, winnerRank: 0 } : s))
        );
      }

      setMsg(`✓ Successfully deleted competition '${targetTitle}' and all associated videos!`);
      await fetchPrizeVideos();
      await fetchSubmissions();
      await fetchCompetitions();

      if (selectedCompetitionModal && (selectedCompetitionModal.competitionId === compId || selectedCompetitionModal.competitionName === compGroup.competitionName)) {
        setSelectedCompetitionModal(null);
      }
    } catch (err) {
      console.error("Failed to delete competition group", err);
      alert("Failed to delete competition entries: " + (err.response?.data?.message || err.response?.data || err.message));
    }
  };

  const handleEditCompetitionGroup = (compGroup) => {
    setIsEditingGroup(true);
    setEditingGroupCompName(compGroup.competitionName || "");
    setEditingGroupCompId(compGroup.competitionId || null);
    setPastVideoForm({
      competitionName: compGroup.competitionName || "",
      year: compGroup.year || new Date().getFullYear().toString(),
      month: compGroup.month || "January",
      competitionType: compGroup.category || "Public Speaking",
      groupCategory: "Group A",
      videoUrl: "",
      winnerRank: "1",
      prizeAmount: "",
    });

    const newBatch = initialBatchWinners.map((template) => {
      const existing = compGroup.videos.find((v) => {
        const cat = (v.groupCategory || "").toLowerCase();
        const tCat = template.groupCategory.toLowerCase();
        const isMatchCat = (cat.includes("group a") && tCat.includes("group a")) ||
                           (cat.includes("group b") && tCat.includes("group b")) ||
                           (cat.includes("group c") && tCat.includes("group c")) ||
                           (cat.includes("group d") && tCat.includes("group d")) ||
                           (cat.includes("consolation") && tCat.includes("consolation"));
        return isMatchCat && Number(v.winnerRank) === Number(template.winnerRank);
      });

      if (existing) {
        return {
          ...template,
          id: existing.id || null,
          prizeAmount: existing.prizeAmount || template.prizeAmount,
          studentName: existing.studentName || "",
          studentClass: existing.studentClass || "",
          schoolName: existing.schoolName || "",
          rollNumber: existing.rollNumber || "",
          videoUrl: existing.videoUrl || "",
        };
      }
      return template;
    });

    const activeConsolation = {};
    const webVisibility = {};
    if (Array.isArray(compGroup.videos)) {
      compGroup.videos.forEach((v) => {
        const isConsol = Boolean(v.isConsolation) || (v.winnerRank && Number(v.winnerRank) > 3);
        if (isConsol && v.groupCategory) {
          activeConsolation[v.groupCategory] = true;
          if (v.showOnWeb === true || v.showOnWeb === 1 || String(v.showOnWeb) === "true") {
            webVisibility[v.groupCategory] = true;
          } else if (v.showOnWeb === false || v.showOnWeb === 0 || String(v.showOnWeb) === "false") {
            webVisibility[v.groupCategory] = false;
          }
        }
      });
    }
    setIncludeConsolation(activeConsolation);
    setConsolationShowOnWeb(webVisibility);

    setBatchWinners(newBatch);
    setShowPastVideoModal(true);
  };

  const handleDeleteIndividualWinner = async (winnerItem) => {
    if (!winnerItem) return;
    if (!window.confirm(`⚠️ Are you sure you want to delete this winner entry (${winnerItem.groupCategory} - Rank ${winnerItem.winnerRank})?`)) {
      return;
    }
    try {
      const isSub = winnerItem.isAnnouncedWinner || (winnerItem.id && (String(winnerItem.id).startsWith("sub-") || String(winnerItem.id).startsWith("winner-sub-")));
      const cleanId = String(winnerItem.id).replace(/^(prize-video-|video-)/, "");
      if (isSub) {
        const subId = String(winnerItem.id).replace(/^(sub-|winner-sub-)/, "");
        await axiosInstance.post(`/admin/school-competitions/submissions/${encodeURIComponent(subId)}/announce-winner`, { winnerRank: 0 });
      } else {
        await axiosInstance.delete(`/admin/school-competitions/prize-videos/${encodeURIComponent(cleanId)}`);
      }

      // Optimistically remove from state
      setPrizeVideos((prev) => prev.filter((p) => String(p.id) !== String(winnerItem.id) && String(p.id) !== String(cleanId)));

      setMsg("✓ Winner entry deleted successfully");
      await fetchPrizeVideos();
      await fetchSubmissions();

      if (selectedCompetitionModal) {
        const updatedVideos = selectedCompetitionModal.videos.filter((v) => String(v.id) !== String(winnerItem.id));
        if (updatedVideos.length === 0) {
          setSelectedCompetitionModal(null);
        } else {
          setSelectedCompetitionModal((prev) => ({ ...prev, videos: updatedVideos }));
        }
      }
    } catch (err) {
      console.error("Failed to delete individual winner", err);
      alert("Failed to delete winner entry: " + (err.response?.data?.message || err.response?.data || err.message));
    }
  };

  const handleSaveEditedIndividualWinner = async (e) => {
    e.preventDefault();
    if (!editingWinnerItem) return;

    try {
      if (!editingWinnerItem.isAnnouncedWinner && editingWinnerItem.id && !editingWinnerItem.id.startsWith("sub-")) {
        const payload = {
          competitionName: editingWinnerItem.competitionName,
          category: editingWinnerItem.category,
          competitionType: editingWinnerItem.category,
          groupCategory: editingWinnerItem.groupCategory,
          winnerRank: parseInt(editingWinnerItem.winnerRank, 10),
          prizeAmount: editingWinnerItem.prizeAmount,
          videoUrl: editingWinnerItem.videoUrl,
          year: editingWinnerItem.year,
          month: editingWinnerItem.month,
          isPastCompetition: true,
        };
        await axiosInstance.put(`/admin/school-competitions/prize-videos/${editingWinnerItem.id}`, payload);
      }
      setMsg("✓ Winner entry updated successfully!");
      await fetchPrizeVideos();
      await fetchSubmissions();
      setEditingWinnerItem(null);
    } catch (err) {
      console.error("Failed to update individual winner", err);
      setMsg("⚠️ Failed to update winner entry: " + (err.response?.data?.message || err.response?.data || err.message || "Unknown error"));
    }
  };

  const getAutoWinnerRank = (sub) => {
    if (!sub) return null;
    // Explicitly reset/deleted winner
    if (sub.winnerRank === 0 || sub.winnerRank === "0") return null;
    if (sub.winnerRank && Number(sub.winnerRank) > 0) return Number(sub.winnerRank);

    const comp = (competitions || []).find((c) => String(c.competitionId) === String(sub.competitionId));
    if (!comp || (comp.winnerAnnouncementMode !== "AUTOMATIC" && comp.winnerAnnouncementMode !== "AUTO")) return null;

    // Ensure submission has been evaluated (must have a valid score > 0)
    const scoreVal = sub.totalScore !== undefined && sub.totalScore !== null ? sub.totalScore : (sub.score || 0);
    if (!scoreVal || scoreVal <= 0) return null;

    // Filter submissions of the same competition, groupCategory, AND classGrade with valid scores
    const groupSubs = (submissions || []).filter(
      (s) =>
        String(s.competitionId) === String(sub.competitionId) &&
        (s.groupCategory === sub.groupCategory || !sub.groupCategory) &&
        (s.classGrade === sub.classGrade || !sub.classGrade) &&
        s.status !== "REJECTED" &&
        s.status !== "DELETED" &&
        s.winnerRank !== 0 &&
        s.winnerRank !== "0" &&
        ((s.totalScore !== undefined && s.totalScore !== null && s.totalScore > 0) || (s.score && s.score > 0))
    );

    if (groupSubs.length === 0) return null;

    // Sort by totalScore / score descending
    const sorted = [...groupSubs].sort((a, b) => {
      const scoreA = a.totalScore !== undefined && a.totalScore !== null ? a.totalScore : (a.score || 0);
      const scoreB = b.totalScore !== undefined && b.totalScore !== null ? b.totalScore : (b.score || 0);
      return scoreB - scoreA;
    });

    // Find the rank (index + 1)
    const idx = sorted.findIndex((s) => (s.submissionId && s.submissionId === sub.submissionId) || (s.id && s.id === sub.id));
    if (idx >= 0 && idx < 3) {
      return idx + 1; // 1, 2, or 3
    }
    return null;
  };

  const allAdminUploadedVideos = useMemo(() => {
    const winnerSubItems = (submissions || [])
      .filter((s) => {
        if (s.winnerRank === 0 || s.winnerRank === "0" || s.status === "DELETED" || s.status === "REJECTED") {
          return false;
        }

        const manualRank = s.winnerRank || s.winner_rank || s.rank;
        const isWinnerFlag = Boolean(s.isWinner) || String(s.isWinner) === "true" || Boolean(s.is_winner) || String(s.is_winner) === "true";
        const isStatusWinner = s.status === "WINNER" || s.status === "APPROVED";
        const hasManualRank = manualRank && Number(manualRank) > 0;

        const autoRank = getAutoWinnerRank(s);
        const hasAutoRank = autoRank && autoRank > 0;

        return isWinnerFlag || isStatusWinner || hasManualRank || hasAutoRank;
      })
      .map((s) => {
        const comp = (competitions || []).find((c) => String(c.competitionId) === String(s.competitionId));
        const url = s.videoUrl || s.fileUrl || s.mediaUrl || s.url || s.submissionFile || s.contentUrl || s.video_url || s.file_url || s.link || s.youtubeUrl || "";
        const computedRank = getAutoWinnerRank(s) || Number(s.winnerRank || s.winner_rank || s.rank) || 1;

        return {
          id: `sub-${s.submissionId || s.id}`,
          competitionId: s.competitionId,
          competitionName: `${comp ? comp.title : (s.competitionTitle || s.competitionName || "Announced Winner")}`,
          category: comp ? comp.category : (s.category || s.competitionType || "General"),
          competitionType: comp ? comp.category : (s.competitionType || "General"),
          groupCategory: s.groupCategory || "Group A",
          winnerRank: computedRank,
          prizeAmount: s.prizeAmount || (computedRank === 1 ? "₹ 5,000" : computedRank === 2 ? "₹ 3,000" : "₹ 1,000"),
          videoUrl: url,
          year: comp && comp.startDate ? new Date(comp.startDate).getFullYear().toString() : new Date().getFullYear().toString(),
          month: comp && comp.startDate ? new Date(comp.startDate).toLocaleString("default", { month: "long" }) : "January",
          isPastCompetition: false,
          isAnnouncedWinner: true,
          studentName: s.studentName || s.userName,
          schoolName: s.schoolName,
        };
      });

    // Strictly exclude official prize ceremony videos from the 4th tab (All Past Uploaded Competition Videos)
    const nonCeremonyPrizeVideos = (prizeVideos || []).filter((v) => {
      if (Boolean(v.isPastCompetition) || String(v.isPastCompetition) === "true") {
        return true;
      }
      if (v.studentName || v.rollNumber || (v.winnerRank && Number(v.winnerRank) > 0)) {
        return true;
      }
      return false;
    });

    return [...nonCeremonyPrizeVideos, ...winnerSubItems];
  }, [prizeVideos, submissions, competitions]);

  const pastCompetitionsCount = useMemo(() => {
    const uniqueCompKeys = new Set(
      allAdminUploadedVideos.map((v) => v.competitionId || v.competitionName).filter(Boolean)
    );
    return uniqueCompKeys.size;
  }, [allAdminUploadedVideos]);

  const ceremonyVideosList = useMemo(() => {
    return (prizeVideos || [])
      .filter((v) => {
        if (!v.videoUrl || !v.videoUrl.trim()) return false;
        // Strictly ceremony videos: must not be past competition batch videos
        if (Boolean(v.isPastCompetition) || String(v.isPastCompetition) === "true") return false;
        // Must not be student winner submission items
        if (Boolean(v.isAnnouncedWinner) || String(v.isAnnouncedWinner) === "true") return false;
        if (v.id && (String(v.id).startsWith("sub-") || String(v.id).startsWith("winner-sub-"))) return false;
        // Must not be individual winner rank entry or student entry
        if (v.winnerRank && Number(v.winnerRank) > 0) return false;
        if (v.studentName || v.rollNumber) return false;
        return true;
      })
      .map((v) => {
        const matchedComp = (competitions || []).find(
          (c) => String(c.competitionId) === String(v.competitionId)
        );
        return {
          ...v,
          resolvedCompetitionName:
            v.competitionName && v.competitionName.trim() && v.competitionName !== v.competitionId
              ? v.competitionName
              : (matchedComp ? matchedComp.title : (v.competitionName || v.competitionId || "Competition")),
          resolvedCategory:
            v.category || (matchedComp ? matchedComp.category : (v.competitionType || "General")),
        };
      });
  }, [prizeVideos, competitions]);

  const handleBatchFileUpload = async (file, index) => {
    if (!file) return;
    setUploadingVideo(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await axiosInstance.post(
        "/admin/school-competitions/prize-videos/upload-video-file",
        formData
      );
      if (res.data && res.data.videoUrl) {
        const uploadedUrl = res.data.videoUrl;
        setBatchWinners((prev) => {
          const updated = [...prev];
          updated[index] = { ...updated[index], videoUrl: uploadedUrl };
          return updated;
        });
        setMsg(`✓ Video file uploaded for ${batchWinners[index].label}!`);
      }
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || err.response?.data || "Failed to upload video file.");
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleFileUploadForVideo = async (file, targetForm) => {
    if (!file) return;
    setUploadingVideo(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await axiosInstance.post(
        "/admin/school-competitions/prize-videos/upload-video-file",
        formData
      );
      if (res.data && res.data.videoUrl) {
        const uploadedUrl = res.data.videoUrl;
        if (targetForm === "PAST") {
          setPastVideoForm((prev) => ({ ...prev, videoUrl: uploadedUrl }));
        } else {
          setPrizeVideoForm((prev) => ({ ...prev, videoUrl: uploadedUrl }));
        }
        setMsg("✓ Video file uploaded successfully!");
      }
    } catch (err) {
      console.error(err);
      alert(err.response?.data || "Failed to upload video file.");
    } finally {
      setUploadingVideo(false);
    }
  };

  // Fetch the persisted list of prize distribution videos from the backend
  const fetchPrizeVideos = async () => {
    setPrizeVideosLoading(true);
    try {
      const res = await axiosInstance.get("/admin/school-competitions/prize-videos");
      if (res.data && Array.isArray(res.data)) {
        setPrizeVideos(res.data);
      }
    } catch (err) {
      console.error("Failed to fetch prize distribution videos", err);
    } finally {
      setPrizeVideosLoading(false);
    }
  };

  const handleOpenPrizeModal = () => {
    setPrizeVideoForm({
      competitionId: "",
      competitionName: "",
      category: "",
      startDate: "",
      endDate: "",
      videoUrl: "",
    });
    setEditingPrizeVideoId(null);
    setIsCreatingPrizeCustomCategory(false);
    setPrizeCustomCategoryName("");
    setShowPrizeCeremonyModal(true);
    fetchPrizeVideos();
  };

  const handleClosePrizeCeremonyModal = () => {
    setShowPrizeCeremonyModal(false);
    setPrizeVideoForm({
      competitionId: "",
      competitionName: "",
      category: "",
      startDate: "",
      endDate: "",
      videoUrl: "",
    });
    setEditingPrizeVideoId(null);
    setIsCreatingPrizeCustomCategory(false);
    setPrizeCustomCategoryName("");
  };

  const handleOpenPastPrizeModal = () => {
    setIsEditingGroup(false);
    setEditingGroupCompName("");
    setEditingGroupCompId(null);
    setPastVideoForm({
      competitionName: "",
      year: new Date().getFullYear().toString(),
      month: "January",
      competitionType: categories[0] || "Public Speaking",
      groupCategory: "Group A",
      videoUrl: "",
      winnerRank: "1",
      prizeAmount: "",
    });
    setEditingPrizeVideoId(null);
    setBatchWinners(initialBatchWinners);
    setIncludeConsolation({});
    setConsolationShowOnWeb({});
    setIsCreatingPastCustomCategory(false);
    setPastCustomCategoryName("");
    setShowPastVideoModal(true);
    fetchPrizeVideos();
  };

  const handleClosePastVideoModal = () => {
    setShowPastVideoModal(false);
    setIsEditingGroup(false);
    setEditingGroupCompName("");
    setEditingGroupCompId(null);
    setPastVideoForm({
      competitionName: "",
      year: new Date().getFullYear().toString(),
      month: "January",
      competitionType: categories[0] || "Public Speaking",
      groupCategory: "Group A",
      videoUrl: "",
      winnerRank: "1",
      prizeAmount: "",
    });
    setEditingPrizeVideoId(null);
    setBatchWinners(initialBatchWinners);
    setIncludeConsolation({});
    setConsolationShowOnWeb({});
    setIsCreatingPastCustomCategory(false);
    setPastCustomCategoryName("");
  };

  const handleClosePrizeModal = () => {
    handleClosePrizeCeremonyModal();
    handleClosePastVideoModal();
  };

  const handleSelectPrizeCompetition = (compId) => {
    const comp = competitions.find((c) => c.competitionId === compId);
    setPrizeVideoForm({
      ...prizeVideoForm,
      competitionId: compId,
      competitionName: comp ? comp.title : "",
      category: comp ? comp.category : "",
      startDate: comp ? toDateInputValue(comp.startDate) : "",
      endDate: comp ? toDateInputValue(comp.endDate) : "",
    });
  };

  const handleSubmitPrizeVideo = async (e) => {
    e.preventDefault();
    if (!prizeVideoForm.competitionId || !prizeVideoForm.competitionId.trim()) {
      alert("⚠️ Please enter a Competition ID!");
      return;
    }
    const rawUrl = (prizeVideoForm.videoUrl || "").trim();
    if (!rawUrl) {
      alert("⚠️ Please paste a YouTube or Google Drive Video URL!");
      return;
    }

    const lower = rawUrl.toLowerCase();
    const isYouTube = lower.includes("youtube.com") || lower.includes("youtu.be");
    const isGoogleDrive = lower.includes("drive.google.com");

    if (!isYouTube && !isGoogleDrive) {
      alert("⚠️ Only YouTube or Google Drive video links are allowed for Prize Distribution Ceremony!");
      return;
    }

    const payload = {
      competitionId: prizeVideoForm.competitionId.trim(),
      competitionName: (prizeVideoForm.competitionName || "").trim(),
      category: (prizeVideoForm.category || "").trim(),
      startDate: prizeVideoForm.startDate || null,
      endDate: prizeVideoForm.endDate || null,
      videoUrl: rawUrl,
      isPastCompetition: false,
    };

    try {
      if (editingPrizeVideoId) {
        try {
          await axiosInstance.put(
            `/admin/school-competitions/ceremony-videos/${editingPrizeVideoId}`,
            payload,
          );
        } catch (ceremonyPutErr) {
          await axiosInstance.put(
            `/admin/school-competitions/prize-videos/${editingPrizeVideoId}`,
            payload,
          );
        }
        setMsg(
          `✓ Prize distribution ceremony video for '${prizeVideoForm.competitionName || prizeVideoForm.competitionId}' updated successfully!`,
        );
      } else {
        try {
          await axiosInstance.post(
            "/admin/school-competitions/ceremony-videos",
            payload,
          );
        } catch (ceremonyPostErr) {
          await axiosInstance.post(
            "/admin/school-competitions/prize-videos",
            payload,
          );
        }
        setMsg(
          `✓ Prize distribution ceremony video for '${prizeVideoForm.competitionName || prizeVideoForm.competitionId}' added successfully!`,
        );
      }

      await fetchPrizeVideos();
      handleClosePrizeCeremonyModal();
    } catch (err) {
      console.error(err);
      const errorMsg =
        (typeof err.response?.data === "string" ? err.response?.data : null) ||
        err.response?.data?.message ||
        err.message ||
        "Failed to save prize distribution ceremony video.";
      alert(`⚠️ ${errorMsg}`);
    }
  };

  const handleSubmitPastVideo = async (e) => {
    e.preventDefault();
    if (!pastVideoForm.competitionName.trim()) {
      alert("⚠️ Please enter Competition Name!");
      return;
    }

    if (editingPrizeVideoId) {
      // Editing a single video entry
      if (!pastVideoForm.videoUrl.trim()) {
        alert("⚠️ Please paste a Video URL!");
        return;
      }
      const payload = {
        competitionName: pastVideoForm.competitionName.trim(),
        year: pastVideoForm.year,
        month: pastVideoForm.month,
        competitionType: pastVideoForm.competitionType.trim(),
        category: pastVideoForm.competitionType.trim(),
        groupCategory: pastVideoForm.groupCategory,
        videoUrl: pastVideoForm.videoUrl.trim(),
        isPastCompetition: true,
        winnerRank: pastVideoForm.winnerRank ? parseInt(pastVideoForm.winnerRank, 10) : null,
        prizeAmount: pastVideoForm.prizeAmount ? pastVideoForm.prizeAmount.trim() : "",
      };

      try {
        const cleanId = String(editingPrizeVideoId).replace(/^(prize-video-|video-)/, "");
        await axiosInstance.put(
          `/admin/school-competitions/prize-videos/${encodeURIComponent(cleanId)}`,
          payload,
        );
        setMsg(`✓ Past competition video '${pastVideoForm.competitionName}' updated successfully!`);
        await fetchPrizeVideos();
        handleClosePastVideoModal();
      } catch (err) {
        console.error(err);
        alert(err.response?.data?.message || err.response?.data || "Failed to update past competition video.");
      }
      return;
    }

    // New Past Competition: Batch upload (restricted to Group C if Kojo Competition)
    const isKojoCompetition = Boolean(
      (pastVideoForm.competitionType && pastVideoForm.competitionType.toLowerCase().includes("kojo")) ||
      (pastVideoForm.competitionName && pastVideoForm.competitionName.toLowerCase().includes("kojo"))
    );

    const allowedWinners = isKojoCompetition
      ? batchWinners.filter((w) => w.groupCategory && w.groupCategory.includes("Group C"))
      : batchWinners;

    const activeWinners = allowedWinners.filter((w) => w.videoUrl && w.videoUrl.trim() !== "");
    if (activeWinners.length === 0) {
      if (isKojoCompetition) {
        setMsg("⚠️ Please upload or paste a Word (.doc/.docx), PDF (.pdf), TXT (.txt), or Image file for at least 1 Winner Rank in Group C!");
      } else {
        setMsg("⚠️ Please upload or paste a Video URL for at least 1 Winner Rank!");
      }
      return;
    }

    if (isKojoCompetition) {
      const hasVideo = activeWinners.some((w) =>
        w.videoUrl && (
          w.videoUrl.includes("youtube.com") ||
          w.videoUrl.includes("youtu.be") ||
          w.videoUrl.match(/\.(mp4|mov|avi|mkv|3gp|webm|wmv|flv)(\?|$)/i) ||
          w.videoUrl.startsWith("data:video")
        )
      );
      if (hasVideo) {
        setMsg("⚠️ Video entries are NOT allowed for Kojo Competition! Please upload Word (.doc, .docx), PDF (.pdf), TXT (.txt) files, or any Image format.");
        return;
      }
    }

    const payload = {
      competitionId: isEditingGroup ? editingGroupCompId : null,
      competitionName: pastVideoForm.competitionName.trim(),
      originalCompetitionName: isEditingGroup ? editingGroupCompName : pastVideoForm.competitionName.trim(),
      year: pastVideoForm.year,
      month: pastVideoForm.month,
      competitionType: pastVideoForm.competitionType.trim(),
      winners: activeWinners.map((w) => {
        let numericId = null;
        if (w.id && !String(w.id).startsWith("sub-")) {
          const num = Number(String(w.id).replace(/\D/g, ""));
          if (!isNaN(num) && num > 0) numericId = num;
        }
        const isConsol = Boolean(w.isConsolation) || (w.winnerRank && Number(w.winnerRank) > 3);
        const showWeb = isConsol ? Boolean(consolationShowOnWeb[w.groupCategory]) : true;
        return {
          id: numericId,
          groupCategory: w.groupCategory,
          winnerRank: w.winnerRank,
          prizeAmount: w.prizeAmount ? w.prizeAmount.trim() : "",
          studentName: w.studentName ? w.studentName.trim() : "",
          studentClass: w.studentClass ? w.studentClass.trim() : "",
          schoolName: w.schoolName ? w.schoolName.trim() : "",
          rollNumber: w.rollNumber ? w.rollNumber.trim() : "",
          videoUrl: w.videoUrl.trim(),
          isConsolation: isConsol,
          showOnWeb: showWeb,
        };
      }),
    };

    try {
      await axiosInstance.post(
        "/admin/school-competitions/prize-videos/batch",
        payload,
      );
      setMsg(`✓ ${isEditingGroup ? "Updated" : "Saved"} ${activeWinners.length} winner videos for '${pastVideoForm.competitionName}' successfully!`);
      await fetchPrizeVideos();
      handleClosePastVideoModal();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || err.response?.data || "Failed to save batch winner videos.");
    }
  };

  const handleEditPrizeVideo = (video) => {
    if (video.isPastCompetition) {
      setPastVideoForm({
        competitionName: video.competitionName || "",
        year: video.year || new Date().getFullYear().toString(),
        month: video.month || "January",
        competitionType: video.competitionType || video.category || "Public Speaking",
        groupCategory: video.groupCategory || "Group A",
        videoUrl: video.videoUrl || "",
        winnerRank: video.winnerRank ? String(video.winnerRank) : "1",
        prizeAmount: video.prizeAmount || "",
      });
      setEditingPrizeVideoId(video.id);
      setShowPastVideoModal(true);
    } else {
      setPrizeVideoForm({
        competitionId: video.competitionId || "",
        competitionName: video.competitionName || "",
        category: video.category || "",
        startDate: toDateInputValue(video.startDate),
        endDate: toDateInputValue(video.endDate),
        videoUrl: video.videoUrl || "",
      });
      setEditingPrizeVideoId(video.id);
      setShowPrizeCeremonyModal(true);
    }
  };

  const renderAdminVideoPreview = (url) => {
    if (!url || !url.trim()) return null;
    const cleanUrl = url.trim();

    // 1. All Image formats (.jpg, .jpeg, .png, .gif, .webp, .bmp, .heic, .heif, .svg, .tiff, etc.)
    const isImage = cleanUrl.startsWith("data:image") || cleanUrl.match(/\.(jpeg|jpg|png|gif|webp|bmp|heic|heif|svg|tiff|tif)(\?|$)/i);
    if (isImage) {
      return (
        <div style={{ marginTop: 12, borderRadius: 8, overflow: "hidden", border: "1px solid var(--sc-border)", background: "#0f172a", maxHeight: 220, textAlign: "center" }}>
          <img
            src={cleanUrl}
            alt="Preview"
            style={{ maxHeight: 220, maxWidth: "100%", objectFit: "contain", display: "inline-block" }}
          />
        </div>
      );
    }

    // 2. PDF Document
    const isPdf = cleanUrl.startsWith("data:application/pdf") || cleanUrl.match(/\.pdf(\?|$)/i);
    if (isPdf) {
      return (
        <div style={{ marginTop: 12, borderRadius: 8, border: "1.5px solid #ef4444", background: "#fef2f2", padding: "16px 14px", textAlign: "center" }}>
          <div style={{ fontSize: "32px", marginBottom: "6px" }}>📄</div>
          <div style={{ fontSize: "13px", fontWeight: "700", color: "#991b1b", marginBottom: "8px" }}>
            PDF Document Attached
          </div>
          <a
            href={cleanUrl}
            target="_blank"
            rel="noreferrer"
            style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "#dc2626", color: "#ffffff", padding: "6px 14px", borderRadius: "6px", fontSize: "12px", fontWeight: "600", textDecoration: "none" }}
          >
            👁️ Open PDF
          </a>
        </div>
      );
    }

    // 3. Word Document / TXT file
    const isWord = cleanUrl.match(/\.(doc|docx|dot|dotx|rtf)(\?|$)/i) || cleanUrl.startsWith("data:application/msword") || cleanUrl.startsWith("data:application/vnd.openxmlformats");
    const isTxt = cleanUrl.match(/\.(txt|text|csv|json)(\?|$)/i) || cleanUrl.startsWith("data:text");
    if (isWord || isTxt) {
      return (
        <div style={{ marginTop: 12, borderRadius: 8, border: "1.5px solid #3b82f6", background: "#eff6ff", padding: "16px 14px", textAlign: "center" }}>
          <div style={{ fontSize: "32px", marginBottom: "6px" }}>{isWord ? "📘" : "📝"}</div>
          <div style={{ fontSize: "13px", fontWeight: "700", color: "#1e40af", marginBottom: "8px" }}>
            {isWord ? "Word Document Attached (.doc / .docx)" : "Text File Attached (.txt)"}
          </div>
          <a
            href={cleanUrl}
            target="_blank"
            rel="noreferrer"
            download
            style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "#2563eb", color: "#ffffff", padding: "6px 14px", borderRadius: "6px", fontSize: "12px", fontWeight: "600", textDecoration: "none" }}
          >
            📥 Download / View File
          </a>
        </div>
      );
    }

    // 4. YouTube Video
    const isYoutube = cleanUrl.includes("youtube.com") || cleanUrl.includes("youtu.be");
    if (isYoutube) {
      let embedUrl = cleanUrl;
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=|shorts\/)([^#\&\?]*).*/;
      const match = cleanUrl.match(regExp);
      if (match && match[2].length === 11) {
        embedUrl = `https://www.youtube.com/embed/${match[2]}`;
      }
      return (
        <div style={{ marginTop: 12, borderRadius: 8, overflow: "hidden", border: "1px solid var(--sc-border)", background: "#000", height: 220 }}>
          <iframe
            width="100%"
            height="100%"
            src={embedUrl}
            title="Video Preview"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
      );
    }

    // 5. Google Drive Video
    const isGoogleDrive = cleanUrl.includes("drive.google.com");
    if (isGoogleDrive) {
      let previewUrl = cleanUrl;
      const gdMatch = cleanUrl.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
      if (gdMatch && gdMatch[1]) {
        previewUrl = `https://drive.google.com/file/d/${gdMatch[1]}/preview`;
      }
      return (
        <div style={{ marginTop: 12, borderRadius: 8, overflow: "hidden", border: "1px solid var(--sc-border)", background: "#000", height: 220 }}>
          <iframe
            width="100%"
            height="100%"
            src={previewUrl}
            title="Google Drive Video Preview"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
      );
    }

    // 6. Direct Video
    return (
      <div style={{ marginTop: 12, borderRadius: 8, overflow: "hidden", border: "1px solid var(--sc-border)", background: "#000", height: 220 }}>
        <video
          src={cleanUrl}
          controls
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      </div>
    );
  };

  const handleDeletePrizeVideo = async (id) => {
    if (!id) return;
    if (
      !window.confirm(
        "Are you sure you want to delete this prize distribution video?",
      )
    )
      return;
    try {
      const cleanId = String(id).replace(/^(prize-video-|video-)/, "");
      try {
        await axiosInstance.delete(`/admin/school-competitions/ceremony-videos/${encodeURIComponent(cleanId)}`);
      } catch (cErr) {
        await axiosInstance.delete(`/admin/school-competitions/prize-videos/${encodeURIComponent(cleanId)}`);
      }

      // Optimistically remove from state so the count and list update immediately!
      setPrizeVideos((prev) =>
        prev.filter((p) => String(p.id) !== String(id) && String(p.id) !== String(cleanId))
      );

      await fetchPrizeVideos();
      setMsg("✓ Prize distribution video deleted successfully!");
    } catch (err) {
      console.error(err);
      alert(err.response?.data || "Failed to delete prize distribution video.");
    }
  };

  const isLiveFlag = (c) =>
    c.isLive === true || c.isLive === 1 || String(c.isLive) === "true";
  const isUpcomingComp = (c) =>
    c.status === "UPCOMING" ||
    (c.startDate && new Date(c.startDate) > new Date());
  const isPastComp = (c) =>
    !isUpcomingComp(c) &&
    (!isLiveFlag(c) ||
      c.status === "COMPLETED" ||
      (c.endDate && new Date(c.endDate) < new Date()));
  const isActiveComp = (c) =>
    !isUpcomingComp(c) && !isPastComp(c) && isLiveFlag(c);

  const statusClass = (c) => {
    if (isUpcomingComp(c)) return "status-upcoming";
    if (isActiveComp(c)) return "status-live";
    return "status-past";
  };

  const rankClass = (sub, isManualMode) => {
    if (isManualMode) {
      if (!sub.winnerRank) return "pending";
      if (sub.winnerRank === 1) return "rank-1";
      if (sub.winnerRank === 2) return "rank-2";
      if (sub.winnerRank === 3) return "rank-3";
    } else {
      const autoRank = getAutoWinnerRank(sub);
      if (autoRank === 1) return "rank-1";
      if (autoRank === 2) return "rank-2";
      if (autoRank === 3) return "rank-3";
    }
    return "";
  };

  return (
    <div className="admin-sc-container">
      <main className="admin-sc-main">
        {/* ============ HEADER ============ */}
        <div className="admin-sc-header-card">
          <div>
            <div className="admin-sc-header-eyebrow">
              Shiksha Sahayak · School Competitions
            </div>
            <h2>Admin Competition Control Center</h2>
            <p>
              Super Admin &amp; State Admin — manage competitions, review
              student submissions, toggle live status, and set winner mode.
            </p>
          </div>
          <div className="admin-sc-header-actions">
            <button
              className={`admin-sc-btn ${showSchoolModal ? "admin-sc-btn-accent" : "admin-sc-btn-outline"}`}
              onClick={() => setShowSchoolModal(true)}
            >
              🏫 Manage Schools{" "}
              <span
                className="admin-sc-stamp"
                style={{ marginLeft: 6, opacity: 0.9 }}
              >
                {masterSchools.length}
              </span>
            </button>
            <button
              className={`admin-sc-btn ${isManageCategoriesModalOpen ? "admin-sc-btn-accent" : "admin-sc-btn-outline"}`}
              onClick={() => setIsManageCategoriesModalOpen(true)}
            >
              🏷️ Manage Categories
            </button>
            <button
              className={`admin-sc-btn ${showModal ? "admin-sc-btn-accent" : "admin-sc-btn-outline"}`}
              onClick={handleOpenCreate}
            >
              + Create New Competition
            </button>
            <button
              className={`admin-sc-btn ${showPastVideoModal ? "admin-sc-btn-accent" : "admin-sc-btn-outline"}`}
              onClick={handleOpenPastPrizeModal}
            >
              📜 + Add Past Competition Video
            </button>
            <button
              className={`admin-sc-btn ${showPrizeCeremonyModal ? "admin-sc-btn-accent" : "admin-sc-btn-outline"}`}
              onClick={handleOpenPrizeModal}
            >
              🎬 + Add Prize Ceremony Video
            </button>
          </div>
        </div>

        {/* ============ TABS ============ */}
        <div className="admin-sc-tabs-nav">
          <button
            className={`admin-sc-tab-btn ${activeTab === "active" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("active");
              setPastCurrentPage(1);
              setSelectedCompetitionFilter("NONE");
            }}
          >
            🏆 Active
            <span className="admin-sc-tab-count">
              {competitions.filter(isActiveComp).length}
            </span>
          </button>

          <button
            className={`admin-sc-tab-btn ${activeTab === "upcoming" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("upcoming");
              setPastCurrentPage(1);
              setSelectedCompetitionFilter("NONE");
            }}
          >
            ⏳ Upcoming
            <span className="admin-sc-tab-count">
              {competitions.filter(isUpcomingComp).length}
            </span>
          </button>

          <button
            className={`admin-sc-tab-btn ${activeTab === "past" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("past");
              setPastCurrentPage(1);
              setSelectedCompetitionFilter("NONE");
            }}
          >
            📜 Past
            <span className="admin-sc-tab-count">
              {competitions.filter(isPastComp).length}
            </span>
          </button>

          <button
            className={`admin-sc-tab-btn ${activeTab === "pastVideos" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("pastVideos");
              setPastCurrentPage(1);
              setSelectedCompetitionFilter("NONE");
              fetchPrizeVideos();
            }}
          >
            🎬 All Past Uploaded Competition Videos
            <span className="admin-sc-tab-count">
              {pastCompetitionsCount}
            </span>
          </button>

          <button
            className={`admin-sc-tab-btn ${activeTab === "ceremonyVideos" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("ceremonyVideos");
              setPastCurrentPage(1);
              setSelectedCompetitionFilter("NONE");
              fetchPrizeVideos();
            }}
          >
            🎬 Prize Ceremony Video
            <span className="admin-sc-tab-count">
              {ceremonyVideosList.length}
            </span>
          </button>
        </div>

        {deleteWarningMsg && (
          <div className="admin-sc-modal-overlay" style={{ zIndex: 9999 }}>
            <div className="admin-sc-modal" style={{ maxWidth: 450, padding: "30px 24px", textAlign: "center", borderRadius: "16px", boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)" }}>
              <div style={{
                width: "60px",
                height: "60px",
                borderRadius: "50%",
                backgroundColor: "#fffbeb",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px auto",
                border: "2px solid #fbbf24",
                color: "#d97706",
                fontSize: "30px"
              }}>
                ⚠️
              </div>
              <h3 style={{ margin: "0 0 10px 0", fontSize: "20px", fontWeight: "600", color: "var(--sc-navy)" }}>
                Warning
              </h3>
              <p style={{ fontSize: "15px", color: "#475569", lineHeight: "1.5", margin: "0 0 24px 0" }}>
                {deleteWarningMsg}
              </p>
              <button
                type="button"
                className="admin-sc-btn admin-sc-btn-accent solid"
                onClick={() => setDeleteWarningMsg("")}
                style={{ width: "120px", padding: "10px 24px", borderRadius: "8px", margin: "0 auto", display: "block", cursor: "pointer" }}
              >
                OK
              </button>
            </div>
          </div>
        )}

        {deleteTargetComp && (
          <div className="admin-sc-modal-overlay" style={{ zIndex: 9999 }}>
            <div className="admin-sc-modal" style={{ maxWidth: 450, padding: "30px 24px", textAlign: "center", borderRadius: "16px", boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)" }}>
              <div style={{
                width: "60px",
                height: "60px",
                borderRadius: "50%",
                backgroundColor: "#fef2f2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px auto",
                border: "2px solid #fca5a5",
                color: "#dc2626",
                fontSize: "30px"
              }}>
                🗑️
              </div>
              <h3 style={{ margin: "0 0 10px 0", fontSize: "20px", fontWeight: "600", color: "var(--sc-navy)" }}>
                Delete Competition?
              </h3>
              <p style={{ fontSize: "15px", color: "#475569", lineHeight: "1.5", margin: "0 0 24px 0" }}>
                Are you sure you want to delete competition <strong>'{deleteTargetComp.title}'</strong>? This action cannot be undone.
              </p>
              <div style={{ display: "flex", justifyContent: "center", gap: "12px" }}>
                <button
                  type="button"
                  className="admin-sc-btn"
                  onClick={() => setDeleteTargetComp(null)}
                  style={{ padding: "10px 20px", borderRadius: "8px", cursor: "pointer", backgroundColor: "#e2e8f0", color: "#475569", border: "none", fontWeight: "600" }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="admin-sc-btn"
                  onClick={async () => {
                    const comp = deleteTargetComp;
                    setDeleteTargetComp(null);
                    try {
                      await axiosInstance.delete(`/admin/school-competitions/${comp.competitionId}`);
                      setCompetitions(competitions.filter((c) => c.competitionId !== comp.competitionId));
                      setMsg(`Competition '${comp.title}' deleted successfully!`);
                    } catch (err) {
                      console.error(err);
                      setDeleteWarningMsg(err.response?.data || "Failed to delete competition.");
                    }
                  }}
                  style={{ padding: "10px 20px", borderRadius: "8px", cursor: "pointer", backgroundColor: "#dc2626", color: "white", border: "none", fontWeight: "600" }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {msg && (
          <div className="admin-sc-modal-overlay" style={{ zIndex: 9999 }}>
            <div className="admin-sc-modal" style={{ maxWidth: 450, padding: "30px 24px", textAlign: "center", borderRadius: "18px", boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)", border: "1px solid #e2e8f0", background: "#ffffff", margin: "auto" }}>
              {(() => {
                const isWarnOrErr = typeof msg === "string" && (msg.startsWith("⚠️") || msg.toLowerCase().includes("failed") || msg.toLowerCase().includes("error"));
                return (
                  <>
                    <div style={{
                      width: "60px",
                      height: "60px",
                      borderRadius: "50%",
                      backgroundColor: isWarnOrErr ? "#fef2f2" : "#ecfdf5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 16px auto",
                      border: isWarnOrErr ? "2px solid #fca5a5" : "2px solid #34d399",
                      color: isWarnOrErr ? "#dc2626" : "#10b981",
                      fontSize: isWarnOrErr ? "28px" : "30px"
                    }}>
                      {isWarnOrErr ? "⚠️" : "✓"}
                    </div>
                    <h3 style={{ margin: "0 0 10px 0", fontFamily: "var(--sc-font-display)", fontSize: "20px", fontWeight: "700", color: isWarnOrErr ? "#991b1b" : "var(--sc-navy)" }}>
                      {isWarnOrErr ? "Notice" : "Success!"}
                    </h3>
                    <p style={{ fontSize: "15px", color: "#475569", lineHeight: "1.5", margin: "0 0 24px 0" }}>
                      {msg}
                    </p>
                    <button
                      type="button"
                      className="admin-sc-btn admin-sc-btn-accent solid"
                      style={{
                        width: "120px",
                        padding: "10px 24px",
                        borderRadius: "8px",
                        margin: "0 auto",
                        display: "block",
                        cursor: "pointer",
                        backgroundColor: isWarnOrErr ? "#dc2626" : undefined,
                        borderColor: isWarnOrErr ? "#dc2626" : undefined
                      }}
                      onClick={() => setMsg("")}
                    >
                      OK
                    </button>
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* SECTION 1: COMPETITIONS TABLE FOR ACTIVE / UPCOMING / PAST */}
        {activeTab !== "pastVideos" && activeTab !== "ceremonyVideos" && (
          <div className="admin-sc-panel-card">
            <h3 className="admin-sc-panel-title">
              {activeTab === "active" && "🏆 Active Competitions"}
              {activeTab === "upcoming" && "⏳ Upcoming Competitions"}
              {activeTab === "past" && "📜 Past Competitions"}
            </h3>

          {(() => {
            const filteredComps = competitions.filter((c) => {
              if (activeTab === "active") return isActiveComp(c);
              if (activeTab === "upcoming") return isUpcomingComp(c);
              if (activeTab === "past") return isPastComp(c);
              return true;
            });

            // Sort latest first (by startDate / createdAt / endDate descending)
            filteredComps.sort((a, b) => {
              const dateA = new Date(a.startDate || a.createdAt || a.endDate || 0).getTime();
              const dateB = new Date(b.startDate || b.createdAt || b.endDate || 0).getTime();
              if (dateB !== dateA) return dateB - dateA;
              return String(b.competitionId || '').localeCompare(String(a.competitionId || ''));
            });

            if (filteredComps.length === 0) {
              return (
                <p className="admin-sc-empty-note">
                  No {activeTab} competitions found.{" "}
                  {activeTab === "active" &&
                    'Click "+ Create New Competition" to create one.'}
                </p>
              );
            }

            const totalItems = filteredComps.length;
            const totalPages = Math.max(1, Math.ceil(totalItems / pastItemsPerPage));
            const validPage = Math.min(pastCurrentPage, totalPages);
            const startIndex = (validPage - 1) * pastItemsPerPage;
            const endIndex = Math.min(startIndex + pastItemsPerPage, totalItems);
            const displayComps = activeTab === "past" ? filteredComps.slice(startIndex, endIndex) : filteredComps;

            return (
              <>
                <div className="admin-sc-card-grid">
                  {displayComps.map((c) => {
                    const isSelected =
                      selectedCompetitionFilter === c.competitionId;
                    const isCompleted = !c.isLive || c.status === "COMPLETED";
                    const submissionCount = submissions.filter(
                      (s) => s.competitionId === c.competitionId,
                    ).length;

                    return (
                      <div
                        key={c.competitionId}
                        className={`admin-sc-comp-card ${statusClass(c)} ${isSelected ? "selected" : ""}`}
                      >
                        {/* CARD HEADER */}
                        <div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: 12,
                            }}
                          >
                            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                              <span className="admin-sc-id-tag">
                                {c.competitionId}
                              </span>
                              <span
                                style={{
                                  backgroundColor: "rgba(108, 92, 231, 0.1)",
                                  color: "rgb(108, 92, 231)",
                                  padding: "3px 8px",
                                  borderRadius: "4px",
                                  fontSize: "11px",
                                  fontWeight: "bold",
                                  textTransform: "uppercase",
                                  border: "1px solid rgba(108, 92, 231, 0.2)"
                                }}
                              >
                                🏷️ {c.category || "General"}
                              </span>
                            </div>
                            <span
                              className={`admin-sc-badge ${c.isLive ? "live" : "ended"}`}
                            >
                              {c.isLive ? "● LIVE" : "● ENDED"}
                            </span>
                          </div>

                          <h4
                            style={{
                              fontFamily: "var(--sc-font-display)",
                              fontSize: 18,
                              fontWeight: 600,
                              color: "var(--sc-ink)",
                              margin: "0 0 12px 0",
                              lineHeight: 1.3,
                            }}
                          >
                            {c.title}
                          </h4>

                          {/* COMPETITION METADATA METRICS */}
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 8,
                              marginBottom: 16,
                            }}
                          >
                            <div className="admin-sc-meta-chip">
                              🏫{" "}
                              <strong style={{ color: "var(--sc-ink)" }}>
                                {c.participatingSchools &&
                                c.participatingSchools.length > 0
                                  ? c.participatingSchools.length
                                  : Array.from(
                                      new Set(
                                        submissions
                                          .filter(
                                            (s) =>
                                              s.competitionId ===
                                                c.competitionId && s.schoolName,
                                          )
                                          .map((s) =>
                                            s.schoolName.trim().toLowerCase(),
                                          ),
                                      ),
                                    ).length}
                              </strong>{" "}
                              Schools
                            </div>
                            <div className="admin-sc-meta-chip">
                              🔑{" "}
                              <span
                                className="admin-sc-stamp"
                                style={{ color: "var(--sc-navy)" }}
                              >
                                {c.verificationCode}
                              </span>
                            </div>
                            <div
                              className={`admin-sc-meta-chip ${c.winnerAnnouncementMode === "AUTOMATIC" ? "mode-auto" : "mode-manual"}`}
                            >
                              ⚡ {c.winnerAnnouncementMode}
                            </div>
                            <div className="admin-sc-meta-chip">
                              📅 {toDateInputValue(c.startDate) || "—"} →{" "}
                              {toDateInputValue(c.endDate) || "—"}
                            </div>
                          </div>
                        </div>

                        {/* CARD ACTION BUTTONS */}
                        <div
                          style={{
                            paddingTop: 14,
                            borderTop: "1px solid var(--sc-border)",
                            display: "flex",
                            flexDirection: "column",
                            gap: 10,
                            marginTop: "auto",
                          }}
                        >
                          {/* SELECT & VIEW SUBMISSIONS PRIMARY BUTTON */}
                          <button
                            className="admin-sc-btn"
                            onClick={() => {
                              setSelectedCompetitionFilter(c.competitionId);
                              setSelectedGroupFilter("ALL");
                              setTimeout(() => {
                                const subSection = document.getElementById(
                                  "student-submissions-section",
                                );
                                if (subSection)
                                  subSection.scrollIntoView({
                                    behavior: "smooth",
                                  });
                              }, 50);
                            }}
                            style={{
                              width: "100%",
                              padding: "10px",
                              backgroundColor: isSelected
                                ? "var(--sc-green)"
                                : "var(--sc-navy)",
                              color: "#fff",
                              fontSize: 13,
                              borderRadius: 8,
                              boxShadow: isSelected
                                ? "0 6px 16px -6px rgba(47,143,91,0.5)"
                                : "none",
                            }}
                          >
                            📥 View Submissions ({submissionCount})
                          </button>

                          {/* EDIT, DELETE, TOGGLE LIVE BUTTONS GROUP */}
                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                              justifyContent: "space-between",
                            }}
                          >
                            <div
                              style={{
                                display: "inline-flex",
                                borderRadius: 8,
                                overflow: "hidden",
                                border: "1px solid var(--sc-border)",
                                flex: 1,
                              }}
                            >
                              <button
                                className="admin-sc-btn admin-sc-btn-warning"
                                onClick={() => handleOpenEdit(c)}
                                title="Edit Competition"
                                style={{ flex: 1, fontSize: 12 }}
                              >
                                ✏️ Edit
                              </button>
                              <button
                                className="admin-sc-btn admin-sc-btn-danger"
                                onClick={() => handleDeleteCompetition(c)}
                                disabled={isCompleted}
                                title={
                                  isCompleted
                                    ? "Over/Completed competitions cannot be deleted"
                                    : "Delete competition"
                                }
                                style={{
                                  flex: 1,
                                  fontSize: 12,
                                  borderLeft: "1px solid var(--sc-border)",
                                }}
                              >
                                🗑️ Delete
                              </button>
                            </div>

                            <button
                              className="admin-sc-btn"
                              onClick={() => toggleLiveStatus(c)}
                              style={{
                                padding: "8px 14px",
                                backgroundColor: c.isLive
                                  ? "var(--sc-ink-soft)"
                                  : "var(--sc-green)",
                                color: "#fff",
                                borderRadius: 8,
                                fontSize: 12,
                              }}
                            >
                              {c.isLive ? "End" : "Make Live"}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* PAGINATION BAR FOR PAST TAB */}
                {activeTab === "past" && totalItems > 0 && (
                  <div className="prize-ceremony-pagination-bar" style={{ marginTop: 24 }}>
                    <div style={{ fontSize: "13px", fontWeight: "600", color: "#64748b" }}>
                      Showing <strong>{startIndex + 1}</strong>–<strong>{endIndex}</strong> of <strong>{totalItems}</strong> past competitions
                      {totalPages > 1 && (
                        <span style={{ marginLeft: "8px", color: "#94a3b8", fontWeight: "500" }}>
                          (Page {validPage} of {totalPages})
                        </span>
                      )}
                    </div>
                    {totalPages > 1 && (
                      <div className="prize-ceremony-pagination-controls">
                        <button
                          type="button"
                          className="prize-ceremony-page-btn"
                          disabled={validPage <= 1}
                          onClick={() => setPastCurrentPage(1)}
                        >
                          «
                        </button>
                        <button
                          type="button"
                          className="prize-ceremony-page-btn"
                          disabled={validPage <= 1}
                          onClick={() => setPastCurrentPage((p) => Math.max(1, p - 1))}
                        >
                          ‹ Prev
                        </button>

                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                          .filter((p) => p === 1 || p === totalPages || Math.abs(p - validPage) <= 2)
                          .map((p, idx, arr) => {
                            const prev = arr[idx - 1];
                            return (
                              <React.Fragment key={p}>
                                {prev && p - prev > 1 && <span style={{ padding: "0 4px", color: "#94a3b8" }}>...</span>}
                                <button
                                  type="button"
                                  className={`prize-ceremony-page-btn ${validPage === p ? "active" : ""}`}
                                  onClick={() => setPastCurrentPage(p)}
                                >
                                  {p}
                                </button>
                              </React.Fragment>
                            );
                          })}

                        <button
                          type="button"
                          className="prize-ceremony-page-btn"
                          disabled={validPage >= totalPages}
                          onClick={() => setPastCurrentPage((p) => Math.min(totalPages, p + 1))}
                        >
                          Next ›
                        </button>
                        <button
                          type="button"
                          className="prize-ceremony-page-btn"
                          disabled={validPage >= totalPages}
                          onClick={() => setPastCurrentPage(totalPages)}
                        >
                          »
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

        {/* SECTION FOR 4TH TAB: ALL PAST VIDEOS */}
        {activeTab === "pastVideos" && (
          <PastCompetitionVideosSection
            allAdminUploadedVideos={allAdminUploadedVideos}
            categories={categories}
            prizeVideosLoading={prizeVideosLoading}
            handleOpenPastPrizeModal={handleOpenPastPrizeModal}
            handleEditCompetitionGroup={handleEditCompetitionGroup}
            handleDeleteCompetitionGroup={handleDeleteCompetitionGroup}
            fetchPrizeVideos={fetchPrizeVideos}
            fetchSubmissions={fetchSubmissions}
            onAddNewCategory={() => setShowAddCategoryModal(true)}
            setMsg={setMsg}
          />
        )}

        {/* SECTION FOR 5TH TAB: PRIZE CEREMONY VIDEOS */}
        {activeTab === "ceremonyVideos" && (
          <PrizeCeremonyVideosSection
            ceremonyVideos={ceremonyVideosList}
            prizeVideosLoading={prizeVideosLoading}
            handleOpenPrizeModal={handleOpenPrizeModal}
            handleEditPrizeVideo={handleEditPrizeVideo}
            handleDeletePrizeVideo={handleDeletePrizeVideo}
            fetchPrizeVideos={fetchPrizeVideos}
            competitions={competitions}
            categories={categories}
            onAddNewCategory={() => setShowAddCategoryModal(true)}
            setMsg={setMsg}
          />
        )}

        {/* SECTION 2: STUDENT SUBMISSIONS TABLE BELOW (APPEARS WHEN ADMIN SELECTS A COMPETITION) */}
        {selectedCompetitionFilter !== "NONE" && activeTab !== "upcoming" && activeTab !== "pastVideos" && activeTab !== "ceremonyVideos" && (
          <div
            id="student-submissions-section"
            className="admin-sc-panel-card no-tab-radius"
          >
            {/* SINGLE LINE ROW FOR HEADING, SEARCH BAR & COMPETITION FILTER */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 16,
                marginBottom: 20,
                flexWrap: "nowrap",
              }}
            >
              <div
                className="admin-sc-panel-title"
                style={{
                  marginBottom: 0,
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <span>📥 Student Submissions</span>
                <span
                  style={{
                    color: "var(--sc-slate)",
                    fontWeight: 500,
                    fontSize: 15,
                  }}
                >
                  (
                  {selectedCompetitionFilter === "ALL"
                    ? "All Entries"
                    : selectedCompetitionFilter}
                  )
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  flex: 1,
                  justifyContent: "flex-end",
                }}
              >
                {/* SEARCH INPUT BY STUDENT NAME, SCHOOL, COMPETITION NAME */}
                <input
                  className="admin-sc-search-input"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setSubPage(1); }}
                  placeholder="🔍 Search Student, School, Competition..."
                  style={{ minWidth: 280, maxWidth: 360, flex: 1 }}
                />

                {/* COMPETITION WISE FILTER DROPDOWN */}
                <select
                  className="admin-sc-filter-select"
                  value={selectedCompetitionFilter}
                  onChange={(e) => { setSelectedCompetitionFilter(e.target.value); setSubPage(1); }}
                >
                  <option value="ALL">
                    All Competitions ({submissions.length})
                  </option>
                  {Array.from(
                    new Set([
                      ...competitions.map((c) => c.competitionId),
                      ...submissions.map((s) => s.competitionId),
                    ]),
                  ).map((compId) => {
                    const comp = competitions.find(
                      (c) => c.competitionId === compId,
                    );
                    const count = submissions.filter(
                      (s) => s.competitionId === compId,
                    ).length;
                    const title = comp ? comp.title : compId;
                    return (
                      <option key={compId} value={compId}>
                        {title} ({compId}) - [{count} entries]
                      </option>
                    );
                  })}
                </select>

                {/* GROUP / CLASS FILTER DROPDOWN */}
                {(() => {
                  const selCompObj = competitions.find((c) => c.competitionId === selectedCompetitionFilter);
                  const isKojoSelected = selCompObj && (
                    (selCompObj.category && selCompObj.category.toLowerCase().includes("kojo")) ||
                    (selCompObj.title && selCompObj.title.toLowerCase().includes("kojo"))
                  );

                  return (
                    <select
                      className="admin-sc-filter-select"
                      value={selectedGroupFilter}
                      onChange={(e) => { setSelectedGroupFilter(e.target.value); setSubPage(1); }}
                      style={{ minWidth: 155 }}
                    >
                      <option value="ALL">{isKojoSelected ? "All Classes" : "All Groups"}</option>
                      {isKojoSelected ? (
                        <>
                          <option value="Class 6">Class 6</option>
                          <option value="Class 7">Class 7</option>
                          <option value="Class 8">Class 8</option>
                        </>
                      ) : (
                        <>
                          <option value="Group A (Class 1-2)">Group A (Class 1-2)</option>
                          <option value="Group B (Class 3-5)">Group B (Class 3-5)</option>
                          <option value="Group C (Class 6-8)">Group C (Class 6-8)</option>
                          <option value="Group D (Class 9-12)">Group D (Class 9-12)</option>
                        </>
                      )}
                    </select>
                  );
                })()}

                {/* EXPORT SUBMISSIONS DATA BUTTON */}
                <button
                  type="button"
                  className="admin-sc-btn"
                  onClick={() => {
                    let temp = selectedCompetitionFilter === "ALL"
                      ? submissions
                      : submissions.filter((s) => s.competitionId === selectedCompetitionFilter);
                    if (selectedGroupFilter !== "ALL") {
                      temp = temp.filter((s) => s.groupCategory === selectedGroupFilter);
                    }
                    if (searchQuery.trim()) {
                      const query = searchQuery.toLowerCase().trim();
                      temp = temp.filter((sub) => {
                        const comp = competitions.find((c) => c.competitionId === sub.competitionId);
                        const compTitle = comp ? comp.title.toLowerCase() : "";
                        return (
                          (sub.studentName && sub.studentName.toLowerCase().includes(query)) ||
                          (sub.schoolName && sub.schoolName.toLowerCase().includes(query)) ||
                          (sub.groupCategory && sub.groupCategory.toLowerCase().includes(query)) ||
                          (sub.entryTitle && sub.entryTitle.toLowerCase().includes(query)) ||
                          (sub.competitionId && sub.competitionId.toLowerCase().includes(query)) ||
                          compTitle.includes(query)
                        );
                      });
                    }
                    handleExportCSV(temp);
                  }}
                  style={{
                    backgroundColor: "var(--sc-green)",
                    color: "#fff",
                    fontWeight: 600,
                    padding: "10px 18px",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    height: "42px",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    boxShadow: "0 4px 12px rgba(46, 204, 113, 0.2)",
                  }}
                >
                  📥 Export Data
                </button>
              </div>
            </div>

            {(() => {
              let filteredSubmissions =
                selectedCompetitionFilter === "ALL"
                  ? submissions
                  : submissions.filter(
                      (s) => s.competitionId === selectedCompetitionFilter,
                    );

              if (selectedGroupFilter !== "ALL") {
                filteredSubmissions = filteredSubmissions.filter(
                  (s) => s.groupCategory === selectedGroupFilter || s.classGrade === selectedGroupFilter,
                );
              }

              if (searchQuery.trim()) {
                const query = searchQuery.toLowerCase().trim();
                filteredSubmissions = filteredSubmissions.filter((sub) => {
                  const comp = competitions.find(
                    (c) => c.competitionId === sub.competitionId,
                  );
                  const compTitle = comp ? comp.title.toLowerCase() : "";
                  return (
                    (sub.studentName &&
                      sub.studentName.toLowerCase().includes(query)) ||
                    (sub.schoolName &&
                      sub.schoolName.toLowerCase().includes(query)) ||
                    (sub.groupCategory &&
                      sub.groupCategory.toLowerCase().includes(query)) ||
                    (sub.entryTitle &&
                      sub.entryTitle.toLowerCase().includes(query)) ||
                    (sub.competitionId &&
                      sub.competitionId.toLowerCase().includes(query)) ||
                    compTitle.includes(query)
                  );
                });
              }

              // Apply sorting by Judge Marks
              if (marksSortOrder !== "NONE") {
                filteredSubmissions = filteredSubmissions.sort((a, b) => {
                  const scoreA = a.totalScore || 0;
                  const scoreB = b.totalScore || 0;
                  if (marksSortOrder === "DESC") return scoreB - scoreA;
                  return scoreA - scoreB;
                });
              }

              const totalSubmissions = filteredSubmissions.length;
              if (totalSubmissions === 0) {
                return (
                  <p className="admin-sc-empty-note">
                    No student submissions found matching your filter.
                  </p>
                );
              }

              const totalPages = Math.ceil(totalSubmissions / subPageSize);
              const activePage = Math.min(subPage, totalPages) || 1;
              const startIndex = (activePage - 1) * subPageSize;
              const paginatedSubmissions = filteredSubmissions.slice(
                startIndex,
                startIndex + subPageSize,
              );

              return (
                <>
                  <div style={{ overflowX: "auto" }}>
                  <table className="admin-sc-table">
                    <thead>
                      <tr>
                        <th>Submission Info</th>
                        <th>Student Details</th>
                        <th>School Name</th>
                        <th>Entry Title</th>
                        <th>Video Link</th>
                        <th 
                          onClick={() => {
                            setMarksSortOrder(prev => prev === "NONE" ? "DESC" : prev === "DESC" ? "ASC" : "NONE");
                            setSubPage(1);
                          }}
                          style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
                          title="Click to sort by Average Score"
                        >
                          ⭐ Judge Marks <span style={{ fontSize: "10px", marginLeft: "4px" }}>{marksSortOrder === "DESC" ? "▼" : marksSortOrder === "ASC" ? "▲" : "↕"}</span>
                        </th>
                        <th>Actions</th>
                        <th style={{ background: "#2563eb", color: "white", borderRadius: "0 8px 8px 0", textAlign: "center", padding: "10px 16px" }}>
                          🏆 Announce Winner
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedSubmissions.map((sub) => (
                        <tr key={sub.submissionId}>
                          <td className="wrap-cell">
                            <div style={{ fontSize: "11px", color: "var(--sc-slate)", fontFamily: "monospace" }}>ID: {sub.submissionId}</div>
                            <div style={{ fontSize: "11px", color: "var(--sc-slate)", fontWeight: 600, marginTop: "4px" }}>Comp: {sub.competitionId}</div>
                            <div style={{ fontSize: "11px", color: "var(--sc-slate)", marginTop: "4px" }}>By: <span style={{ color: "var(--sc-navy)" }}>{sub.submittedBy || "N/A"}</span></div>
                          </td>
                          <td className="wrap-cell" style={{ minWidth: "180px" }}>
                            <div style={{ fontWeight: 700, color: "var(--sc-ink)" }}>{sub.studentName}</div>
                            <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                              {sub.classGrade} (Roll: {sub.rollNumber})
                            </div>
                            <div style={{ marginTop: "4px" }}>
                              <span className="admin-sc-group-chip" style={{ fontSize: "11px", padding: "2px 6px" }}>
                                {sub.groupCategory || "Group A (Class 1-2)"}
                              </span>
                            </div>
                          </td>
                          <td className="wrap-cell">{sub.schoolName}</td>
                          <td className="wrap-cell">{sub.entryTitle}</td>
                          <td className="nowrap-cell" style={{ textAlign: "center" }}>
                            {sub.videoUrl ? (
                              (sub.videoUrl.startsWith("data:") && !sub.videoUrl.startsWith("data:image") && !sub.videoUrl.startsWith("data:video")) ? (
                                <button
                                  className="admin-sc-btn-play"
                                  style={{ backgroundColor: "#8b5cf6" }}
                                  onClick={() => setPlayingVideoUrl(sub.videoUrl)}
                                >
                                  📄 View Document
                                </button>
                              ) : (!sub.videoUrl.startsWith("data:") && !sub.videoUrl.startsWith("http")) ? (
                                <button
                                  className="admin-sc-btn-play"
                                  style={{ backgroundColor: "#059669" }}
                                  onClick={() => setPlayingVideoUrl(sub.videoUrl)}
                                >
                                  📝 View Code
                                </button>
                              ) : (sub.videoUrl.startsWith("data:image") || sub.videoUrl.match(/\.(jpeg|jpg|gif|png)$/i)) ? (
                                <button
                                  className="admin-sc-btn-play"
                                  style={{ backgroundColor: "#0284c7" }}
                                  onClick={() => setPlayingVideoUrl(sub.videoUrl)}
                                >
                                  🖼️ View Image
                                </button>
                              ) : (
                                <button
                                  className="admin-sc-btn-play"
                                  onClick={() => setPlayingVideoUrl(sub.videoUrl)}
                                >
                                  ▶ Play Media
                                </button>
                              )
                            ) : (
                              <span style={{ color: "var(--sc-slate-soft)", fontSize: 12 }}>
                                No Media
                              </span>
                            )}
                          </td>
                          <td>
                            {sub.evaluations && sub.evaluations.length > 0 ? (
                              <div>
                                <div 
                                  style={{ 
                                    display: "flex", 
                                    alignItems: "center", 
                                    justifyContent: "space-between", 
                                    padding: "6px 10px", 
                                    background: "#f8fafc", 
                                    borderRadius: "8px",
                                    border: "1px solid #e2e8f0",
                                    cursor: "pointer",
                                    transition: "background 0.2s"
                                  }}
                                  onClick={() => setExpandedSubmissions(prev => ({
                                    ...prev,
                                    [sub.submissionId]: !prev[sub.submissionId]
                                  }))}
                                  onMouseOver={(e) => e.currentTarget.style.background = "#f1f5f9"}
                                  onMouseOut={(e) => e.currentTarget.style.background = "#f8fafc"}
                                >
                                  <div style={{ fontSize: 13, fontWeight: "700", color: "var(--sc-navy)" }}>
                                    Total Score: ⭐ {sub.evaluations.reduce((acc, ev) => acc + (ev.totalScore || 0), 0)} / {sub.evaluations.length * 25}
                                  </div>
                                  <div style={{ fontSize: 11, color: "var(--sc-slate-soft)", fontStyle: "italic" }}>
                                    {expandedSubmissions[sub.submissionId] ? "Hide" : "Click to view"}
                                  </div>
                                </div>

                                {expandedSubmissions[sub.submissionId] && (
                                  <div style={{ marginTop: "12px", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", background: "#ffffff" }}>
                                    {sub.evaluations.map((ev, idx) => (
                                      <div key={idx} style={{ fontSize: 12, marginBottom: 8, borderBottom: idx < sub.evaluations.length - 1 ? "1px dashed #cbd5e1" : "none", paddingBottom: 8 }}>
                                        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", alignItems: "center" }}>
                                          <span className="admin-sc-badge evaluated" style={{ fontSize: 11, padding: "3px 6px", display: "inline-block", background: "#f0fdf4", color: "#166534", border: "1px solid #bbf7d0" }}>
                                            Judge {idx + 1} ({ev.judgeId.split("@")[0]}): ⭐ {ev.totalScore}/25
                                          </span>
                                        </div>
                                        {ev.remarks && (
                                          <div style={{ fontStyle: "italic", color: "#475569", marginTop: 4, paddingLeft: "8px", borderLeft: "2px solid #cbd5e1" }}>
                                            "{ev.remarks}"
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: "2px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: "800", fontSize: 13, color: "var(--sc-navy)" }}>
                                      <span>Average Score:</span>
                                      <span style={{ color: "#2563eb" }}>⭐ {sub.totalScore !== undefined && sub.totalScore !== null ? sub.totalScore : (Math.round(sub.evaluations.reduce((acc, ev) => acc + (ev.totalScore || 0), 0) / sub.evaluations.length * 10) / 10)} / 25</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : sub.totalScore !== undefined && sub.totalScore !== null ? (
                              <div>
                                <span
                                  className="admin-sc-badge evaluated"
                                  style={{ fontSize: 12 }}
                                >
                                  ⭐ {sub.totalScore} / 25 Marks
                                </span>
                                {sub.judgeRemarks && (
                                  <div
                                    style={{
                                      fontSize: 11,
                                      color: "var(--sc-slate)",
                                      marginTop: 4,
                                      fontStyle: "italic",
                                    }}
                                  >
                                    "{sub.judgeRemarks}"
                                  </div>
                                )}
                              </div>
                            ) : sub.status === "COMPLETED" ? (
                              <span
                                className="admin-sc-badge evaluated"
                                style={{ fontSize: 12 }}
                              >
                                ✓ Evaluated
                              </span>
                            ) : (
                              <span
                                style={{
                                  color: "var(--sc-slate-soft)",
                                  fontSize: 12,
                                  fontStyle: "italic",
                                }}
                              >
                                Not Graded Yet
                              </span>
                            )}
                          </td>
                          <td>
                            <div style={{ display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap" }}>
                              {sub.status === "REJECTED" ? (
                                <div>
                                  <span
                                    className="admin-sc-badge rejected"
                                    style={{
                                      display: "inline-block",
                                      marginBottom: 4,
                                    }}
                                  >
                                    🚫 REJECTED
                                  </span>
                                  {sub.rejectionReason && (
                                    <div
                                      style={{
                                        fontSize: 11,
                                        color: "var(--sc-red-text)",
                                        fontWeight: 600,
                                      }}
                                    >
                                      Reason: {sub.rejectionReason}
                                    </div>
                                  )}
                                  {sub.rejectedBy && (
                                    <div
                                      style={{
                                        fontSize: 10,
                                        color: "var(--sc-slate)",
                                      }}
                                    >
                                      By: {sub.rejectedBy}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <button
                                  className="admin-sc-btn admin-sc-btn-danger"
                                  onClick={() =>
                                    handleOpenRejectModal(sub.submissionId)
                                  }
                                  title="Reject Student Submission"
                                  style={{ fontSize: 12, padding: "5px 10px" }}
                                >
                                  🚫 Reject
                                </button>
                              )}
                              <button
                                className="admin-sc-btn"
                                onClick={() => handleDeleteSubmission(sub.submissionId)}
                                title="Delete Submission"
                                style={{
                                  fontSize: 12,
                                  padding: "5px 8px",
                                  backgroundColor: "#fef2f2",
                                  color: "#dc2626",
                                  border: "1px solid #fecaca",
                                  borderRadius: "6px",
                                  cursor: "pointer",
                                }}
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                          <td>
                            {sub.status === "REJECTED" ? (
                              <span
                                style={{
                                  fontSize: 11,
                                  color: "var(--sc-slate-soft)",
                                  fontStyle: "italic",
                                }}
                              >
                                Not Eligible (Rejected)
                              </span>
                            ) : (
                              (() => {
                                const comp = competitions.find(
                                  (c) => c.competitionId === sub.competitionId,
                                );
                                const mode = comp
                                  ? comp.winnerAnnouncementMode
                                  : "AUTOMATIC";
                                const isManualMode =
                                  mode === "MANUAL" || mode === "CHOICE";
                                const isAutomaticMode =
                                  mode === "AUTOMATIC" || mode === "AUTO";

                                if (isAutomaticMode) {
                                  const autoRank = getAutoWinnerRank(sub);
                                  if (autoRank) {
                                    const badges = {
                                      1: "🥇 1st (Auto)",
                                      2: "🥈 2nd (Auto)",
                                      3: "🥉 3rd (Auto)",
                                    };
                                    return (
                                      <div>
                                        <span className={`admin-sc-badge winner rank-${autoRank}`} style={{ fontSize: "12px", padding: "6px 12px", borderRadius: "12px", fontWeight: "bold" }}>
                                          {badges[autoRank]}
                                        </span>
                                      </div>
                                    );
                                  }
                                  return (
                                    <div>
                                      <span
                                        className="admin-sc-meta-chip"
                                        style={{
                                          fontStyle: "italic",
                                          fontSize: 11,
                                          whiteSpace: "nowrap",
                                          display: "inline-block",
                                        }}
                                      >
                                        🤖 Auto (No Rank)
                                      </span>
                                    </div>
                                  );
                                }

                                const takenRanks = submissions
                                  .filter((s) =>
                                    s.competitionId === sub.competitionId &&
                                    (s.classGrade ? s.classGrade === sub.classGrade : s.groupCategory === sub.groupCategory) &&
                                    s.submissionId !== sub.submissionId &&
                                    s.winnerRank !== null &&
                                    s.winnerRank !== undefined
                                  )
                                  .map((s) => String(s.winnerRank));

                                return (
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 4,
                                    }}
                                  >
                                    {isManualMode && !sub.winnerRank && (
                                      <span style={{ 
                                        display: "inline-block", 
                                        padding: "4px 8px", 
                                        borderRadius: "6px", 
                                        background: "linear-gradient(135deg, #f59e0b, #d97706)", 
                                        color: "white", 
                                        fontSize: "10px", 
                                        fontWeight: "700", 
                                        letterSpacing: "0.5px",
                                        boxShadow: "0 2px 4px rgba(245, 158, 11, 0.2)",
                                        marginBottom: "4px",
                                        textAlign: "center"
                                      }}>
                                        ⚡ SELECT WINNER
                                      </span>
                                    )}
                                    <select
                                      className={`admin-sc-medal-select ${rankClass(sub, isManualMode)}`}
                                      value={sub.winnerRank || ""}
                                      onChange={(e) =>
                                        handleAnnounceWinner(
                                          sub.submissionId,
                                          e.target.value,
                                        )
                                      }
                                    >
                                      <option value="">
                                        -- Choose Winner --
                                      </option>
                                      {(!takenRanks.includes("1") || String(sub.winnerRank) === "1") && (
                                        <option value="1">
                                          🥇 1st Place Winner
                                        </option>
                                      )}
                                      {(!takenRanks.includes("2") || String(sub.winnerRank) === "2") && (
                                        <option value="2">
                                          🥈 2nd Place Winner
                                        </option>
                                      )}
                                      {(!takenRanks.includes("3") || String(sub.winnerRank) === "3") && (
                                        <option value="3">
                                          🥉 3rd Place Winner
                                        </option>
                                      )}
                                    </select>
                                  </div>
                                );
                              })()
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* PAGINATION CONTROLS */}
                {totalPages > 1 && (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "16px", padding: "12px 16px", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px solid #cbd5e1", flexWrap: "wrap", gap: "12px" }}>
                    <div style={{ fontSize: "13px", color: "var(--sc-slate)", fontWeight: "600" }}>
                      Showing {startIndex + 1} to {Math.min(startIndex + subPageSize, totalSubmissions)} of {totalSubmissions} submissions
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <button
                        type="button"
                        disabled={activePage === 1}
                        onClick={() => setSubPage((prev) => Math.max(prev - 1, 1))}
                        style={{
                          padding: "6px 12px",
                          borderRadius: "6px",
                          border: "1px solid #cbd5e1",
                          backgroundColor: activePage === 1 ? "#f1f5f9" : "#fff",
                          color: activePage === 1 ? "#94a3b8" : "var(--sc-navy)",
                          cursor: activePage === 1 ? "not-allowed" : "pointer",
                          fontWeight: "600",
                          fontSize: "13px"
                        }}
                      >
                        ◀ Prev
                      </button>
                      {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((pageNum) => (
                        <button
                          key={pageNum}
                          type="button"
                          onClick={() => setSubPage(pageNum)}
                          style={{
                            padding: "6px 12px",
                            borderRadius: "6px",
                            border: pageNum === activePage ? "1px solid #2563eb" : "1px solid #cbd5e1",
                            backgroundColor: pageNum === activePage ? "#2563eb" : "#fff",
                            color: pageNum === activePage ? "#fff" : "#334155",
                            fontWeight: "700",
                            fontSize: "13px",
                            cursor: "pointer"
                          }}
                        >
                          {pageNum}
                        </button>
                      ))}
                      <button
                        type="button"
                        disabled={activePage === totalPages}
                        onClick={() => setSubPage((prev) => Math.min(prev + 1, totalPages))}
                        style={{
                          padding: "6px 12px",
                          borderRadius: "6px",
                          border: "1px solid #cbd5e1",
                          backgroundColor: activePage === totalPages ? "#f1f5f9" : "#fff",
                          color: activePage === totalPages ? "#94a3b8" : "var(--sc-navy)",
                          cursor: activePage === totalPages ? "not-allowed" : "pointer",
                          fontWeight: "600",
                          fontSize: "13px"
                        }}
                      >
                        Next ▶
                      </button>
                    </div>
                  </div>
                )}
              </>
            );
          })()}
          </div>
        )}

        {/* PLAYING MEDIA MODAL (YOUTUBE / DIRECT VIDEO / IMAGE PREVIEW) */}
        {playingVideoUrl && (
          <div
            className="admin-sc-modal-overlay"
            style={{ backgroundColor: "rgba(10,14,24,0.9)", zIndex: 2000 }}
            onClick={() => setPlayingVideoUrl(null)}
          >
            <div
              className="admin-sc-video-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="admin-sc-video-modal-header">
                <span>
                  {(!playingVideoUrl.startsWith("http") && !playingVideoUrl.startsWith("data:")) ? "📝 Source Code Preview" : (playingVideoUrl.startsWith("data:") && !playingVideoUrl.startsWith("data:image") && !playingVideoUrl.startsWith("data:video")) ? "📄 Document File Submission" : "▶ Video Submission Preview"}
                </span>
                <button
                  className="admin-sc-btn admin-sc-btn-danger solid"
                  onClick={() => setPlayingVideoUrl(null)}
                  style={{ borderRadius: 8, padding: "6px 14px" }}
                >
                  Close ✕
                </button>
              </div>

              {(!playingVideoUrl.startsWith("http") && !playingVideoUrl.startsWith("data:")) ? (
                <div style={{ backgroundColor: "#f8fafc", padding: "16px", borderRadius: "8px", border: "1px solid #cbd5e1", maxHeight: "450px", overflowY: "auto", textAlign: "left" }}>
                  <pre style={{ margin: 0, fontFamily: "monospace", fontSize: "14px", whiteSpace: "pre-wrap", color: "#334155", lineHeight: "1.5" }}>
                    {playingVideoUrl}
                  </pre>
                </div>
              ) : (playingVideoUrl.startsWith("data:") && !playingVideoUrl.startsWith("data:image") && !playingVideoUrl.startsWith("data:video")) ? (
                <div style={{ textAlign: "center", padding: "30px 10px", backgroundColor: "#f8fafc", borderRadius: "8px" }}>
                  <div style={{ fontSize: "64px", marginBottom: "15px" }}>📄</div>
                  <h4 style={{ color: "#0f172a", marginBottom: "20px" }}>Document File Submission (.pdf, .csv, .txt, .json, .docx, .xlsx, etc.)</h4>
                  <a
                    href={playingVideoUrl}
                    download="kojo_submission_file"
                    style={{
                      display: "inline-block",
                      backgroundColor: "#2563eb",
                      color: "white",
                      padding: "12px 30px",
                      borderRadius: "8px",
                      fontWeight: "600",
                      textDecoration: "none",
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)"
                    }}
                  >
                    📥 Download Submission File
                  </a>
                </div>
              ) : (playingVideoUrl.startsWith("data:image") || playingVideoUrl.match(/\.(jpeg|jpg|gif|png|webp|bmp|heic)$/i)) ? (
                <div style={{ textAlign: "center", backgroundColor: "#0f172a", padding: 10, borderRadius: 8 }}>
                  <img
                    src={playingVideoUrl}
                    alt="Submission Preview"
                    style={{ maxHeight: 500, maxWidth: "100%", objectFit: "contain", borderRadius: 6 }}
                  />
                </div>
              ) : (getEmbedUrl(playingVideoUrl) && getEmbedUrl(playingVideoUrl).startsWith("https://www.youtube.com/embed/")) ? (
                <div
                  style={{
                    position: "relative",
                    paddingBottom: "56.25%",
                    height: 0,
                    overflow: "hidden",
                    borderRadius: 10,
                  }}
                >
                  <iframe
                    src={getEmbedUrl(playingVideoUrl)}
                    title="Video Player"
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      border: 0,
                    }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              ) : (
                <AsyncVideoPlayer videoUrl={playingVideoUrl} halfScreen={true} />
              )}
            </div>
          </div>
        )}
        {showModal && (
          <div className="admin-sc-modal-overlay" style={{ paddingTop: "100px", paddingBottom: "40px", alignItems: "flex-start", overflowY: "auto" }}>
            <div className="admin-sc-modal hide-scrollbar">
              <button
                type="button"
                className="rotate-close-btn"
                onClick={handleCloseCreate}
                title="Close Modal"
                style={{
                  position: "sticky",
                  top: -18,
                  float: "right",
                  marginTop: -20,
                  marginRight: -24,
                  zIndex: 20,
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  color: "#ffffff",
                  border: "2px solid #ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 18,
                  fontWeight: 700,
                  cursor: "pointer",
                  boxShadow: "0 4px 12px rgba(20,29,51,0.3)",
                }}
              >
                ✕
              </button>

              <h3 className="admin-sc-modal-title">
                ➕ Create New School Competition
              </h3>
              <form
                onSubmit={handleCreateCompetition}
                style={{ display: "flex", flexDirection: "column", gap: 16 }}
              >
                <div>
                  <label className="admin-sc-field-label">Category *</label>
                  <select
                    className="admin-sc-filter-select"
                    value={isCreatingCustomCategory ? "ADD_NEW" : newComp.category}
                    onChange={(e) => {
                      if (e.target.value === "ADD_NEW") {
                        setIsCreatingCustomCategory(true);
                        setNewComp({ ...newComp, category: "" });
                      } else {
                        setIsCreatingCustomCategory(false);
                        setNewComp({ ...newComp, category: e.target.value });
                      }
                    }}
                    style={{ width: "100%", backgroundColor: "#fff" }}
                  >
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                    <option value="ADD_NEW">+ Add New Category</option>
                  </select>
                  {isCreatingCustomCategory && (
                    <div style={{ marginTop: "10px", display: "flex", gap: "8px", alignItems: "flex-end" }}>
                      <div style={{ flex: 1 }}>
                        <label className="admin-sc-field-label">New Category Name *</label>
                        <input
                          type="text"
                          className="admin-sc-input"
                          value={customCategoryName}
                          onChange={(e) => setCustomCategoryName(e.target.value)}
                          placeholder="e.g. Art, Drawing, Essay Writing, etc."
                          style={{ width: "100%" }}
                        />
                      </div>
                      <button
                        type="button"
                        className="admin-sc-btn admin-sc-btn-primary"
                        onClick={() => {
                          const val = customCategoryName.trim();
                          if (val) {
                            if (!categories.includes(val)) {
                              setCategories([...categories, val]);
                            }
                            setNewComp({ ...newComp, category: val });
                            setIsCreatingCustomCategory(false);
                            setCustomCategoryName("");
                          } else {
                            alert("Please enter a valid category name.");
                          }
                        }}
                        style={{ padding: "10px 16px", height: "42px", display: "flex", alignItems: "center", justifyContent: "center" }}
                      >
                        Add
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <label className="admin-sc-field-label">
                    Competition ID * (e.g. COMP-2026-AUG-001)
                  </label>
                  <input
                    className="admin-sc-input admin-sc-id-input"
                    required
                    type="text"
                    value={newComp.competitionId}
                    onChange={(e) =>
                      setNewComp({ ...newComp, competitionId: e.target.value })
                    }
                    placeholder="COMP-2026-AUG-001"
                    style={{ width: "100%" }}
                  />
                </div>

                <div>
                  <label className="admin-sc-field-label">
                    Competition Title *
                  </label>
                  <input
                    className="admin-sc-input"
                    required
                    type="text"
                    value={newComp.title}
                    onChange={(e) =>
                      setNewComp({ ...newComp, title: e.target.value })
                    }
                    placeholder="e.g. Public Speaking Competition"
                    style={{ width: "100%" }}
                  />
                </div>

                <div>
                  <label className="admin-sc-field-label">Description</label>
                  <textarea
                    className="admin-sc-textarea"
                    rows="3"
                    value={newComp.description}
                    onChange={(e) =>
                      setNewComp({ ...newComp, description: e.target.value })
                    }
                    placeholder="Enter competition guidelines"
                    style={{ width: "100%" }}
                  />
                </div>

                {/* SELECT PARTICIPATING SCHOOLS FROM SCHOOL LIST */}
                <div className="admin-sc-form-section">
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 10,
                    }}
                  >
                    <label
                      style={{
                        display: "block",
                        fontSize: 14,
                        fontWeight: 700,
                        color: "var(--sc-ink)",
                      }}
                    >
                      Select Participating Schools
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowSchoolModal(true)}
                      style={{
                        fontSize: 12,
                        color: "var(--sc-navy)",
                        fontWeight: 700,
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                    >
                      + Manage / Add Schools
                    </button>
                  </div>

                  {(() => {
                    const availableSchools = masterSchools.filter(
                      (sch) => !schoolList.includes(sch.name),
                    );
                    
                    return (
                      <div style={{ border: "1px solid #cbd5e1", borderRadius: "8px", padding: "10px", maxHeight: "300px", overflowY: "auto", backgroundColor: "#fff" }}>
                        {availableSchools.length > 0 ? (
                          <>
                            <div style={{ padding: "4px 0", borderBottom: "1px solid #e2e8f0", marginBottom: "4px", display: "flex", alignItems: "center", gap: "8px" }}>
                              <input 
                                type="checkbox" 
                                id="selectAllSchools"
                                checked={false}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSchoolList(masterSchools.map((s) => s.name));
                                  }
                                }}
                                style={{ cursor: "pointer", width: "16px", height: "16px" }}
                              />
                              <label htmlFor="selectAllSchools" style={{ fontWeight: "700", color: "var(--sc-green)", cursor: "pointer", fontSize: "13px" }}>
                                Select All Remaining ({availableSchools.length})
                              </label>
                            </div>
                            {availableSchools.map((sch) => (
                              <div key={sch.id} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "4px 0" }}>
                                <input 
                                  type="checkbox"
                                  id={`school_${sch.id}`}
                                  checked={false}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSchoolList([...schoolList, sch.name]);
                                    }
                                  }}
                                  style={{ cursor: "pointer", width: "14px", height: "14px" }}
                                />
                                <label htmlFor={`school_${sch.id}`} style={{ cursor: "pointer", fontSize: "13px", color: "var(--sc-navy)", flex: 1 }}>
                                  {sch.name}
                                </label>
                              </div>
                            ))}
                          </>
                        ) : (
                          <div style={{ fontSize: "13px", color: "var(--sc-slate)", textAlign: "center", padding: "10px 0" }}>
                            All schools are selected!
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Selected School Chips */}
                  <div style={{ marginTop: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "var(--sc-navy)" }}>Selected Schools ({schoolList.length})</span>
                      {schoolList.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setSchoolList([])}
                          style={{
                            fontSize: "11px",
                            fontWeight: "bold",
                            color: "#ef4444",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            textDecoration: "underline"
                          }}
                        >
                          Clear All
                        </button>
                      )}
                    </div>
                    {schoolList.length > 0 ? (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {schoolList.map((s, idx) => (
                          <span key={idx} className="admin-sc-chip">
                            {s}
                            <button
                              type="button"
                              className="admin-sc-chip-remove"
                              onClick={() => handleRemoveSchool(s)}
                            >
                              ✕
                            </button>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span
                        style={{
                          fontSize: 12,
                          color: "var(--sc-slate)",
                          display: "block",
                        }}
                      >
                        No schools selected yet. Choose schools from list above.
                      </span>
                    )}
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 16,
                  }}
                >
                  <div>
                    <label className="admin-sc-field-label">Start Date *</label>
                    <input
                      className="admin-sc-input"
                      required
                      type="date"
                      value={newComp.startDate}
                      onChange={(e) =>
                        setNewComp({ ...newComp, startDate: e.target.value })
                      }
                      style={{ width: "100%" }}
                    />
                  </div>
                  <div>
                    <label className="admin-sc-field-label">End Date *</label>
                    <input
                      className="admin-sc-input"
                      required
                      type="date"
                      value={newComp.endDate}
                      onChange={(e) =>
                        setNewComp({ ...newComp, endDate: e.target.value })
                      }
                      style={{ width: "100%" }}
                    />
                  </div>
                </div>

                <div>
                  <label className="admin-sc-field-label">
                    Single Shared School Verification Code *
                  </label>
                  <input
                    className="admin-sc-input admin-sc-code-input"
                    required
                    type="text"
                    value={newComp.verificationCode}
                    onChange={(e) =>
                      setNewComp({
                        ...newComp,
                        verificationCode: e.target.value,
                      })
                    }
                    placeholder="e.g. SG-SCHOOL-2026"
                    style={{ width: "100%" }}
                  />
                </div>

                <div>
                  <label className="admin-sc-field-label">
                    Winner Announcement Mode
                  </label>
                  <select
                    className="admin-sc-filter-select"
                    value={newComp.winnerAnnouncementMode}
                    onChange={(e) =>
                      setNewComp({
                        ...newComp,
                        winnerAnnouncementMode: e.target.value,
                      })
                    }
                    style={{ width: "100%", backgroundColor: "#fff" }}
                  >
                    <option value="AUTOMATIC">
                      AUTOMATIC (Auto-rank top 3)
                    </option>
                    <option value="MANUAL">
                      MANUAL (Admin manual trigger)
                    </option>
                  </select>
                </div>

                <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                  <button
                    type="button"
                    className="admin-sc-btn admin-sc-btn-ghost"
                    onClick={() => setShowModal(false)}
                    style={{ flex: 1, padding: 13 }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="admin-sc-btn admin-sc-btn-primary"
                    style={{ flex: 1, padding: 13 }}
                  >
                    Create Competition
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* EDIT COMPETITION MODAL */}
        {showEditModal && editingComp && (
          <div className="admin-sc-modal-overlay">
            <div className="admin-sc-modal accent-amber hide-scrollbar">
              <button
                type="button"
                className="rotate-close-btn"
                onClick={() => {
                  setShowEditModal(false);
                  setEditingComp(null);
                  setSchoolList([]);
                  setIsEditingCustomCategory(false);
                  setEditCustomCategoryName("");
                }}
                title="Close Modal"
                style={{
                  position: "sticky",
                  top: -18,
                  float: "right",
                  marginTop: -20,
                  marginRight: -24,
                  zIndex: 20,
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  color: "#ffffff",
                  border: "2px solid #ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 18,
                  fontWeight: 700,
                  cursor: "pointer",
                  boxShadow: "0 4px 12px rgba(20,29,51,0.3)",
                }}
              >
                ✕
              </button>

              <h3 className="admin-sc-modal-title">
                ✏️ Edit School Competition
              </h3>
              <form
                onSubmit={handleUpdateCompetition}
                style={{ display: "flex", flexDirection: "column", gap: 16 }}
              >
                <div>
                  <label className="admin-sc-field-label">Category *</label>
                  <select
                    className="admin-sc-filter-select"
                    value={isEditingCustomCategory ? "ADD_NEW" : editingComp.category}
                    onChange={(e) => {
                      if (e.target.value === "ADD_NEW") {
                        setIsEditingCustomCategory(true);
                        setEditingComp({ ...editingComp, category: "" });
                      } else {
                        setIsEditingCustomCategory(false);
                        setEditingComp({ ...editingComp, category: e.target.value });
                      }
                    }}
                    style={{ width: "100%", backgroundColor: "#fff" }}
                  >
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                    {editingComp.category && !categories.includes(editingComp.category) && (
                      <option value={editingComp.category}>{editingComp.category}</option>
                    )}
                    <option value="ADD_NEW">+ Add New Category</option>
                  </select>
                  {isEditingCustomCategory && (
                    <div style={{ marginTop: "10px", display: "flex", gap: "8px", alignItems: "flex-end" }}>
                      <div style={{ flex: 1 }}>
                        <label className="admin-sc-field-label">New Category Name *</label>
                        <input
                          type="text"
                          className="admin-sc-input"
                          value={editCustomCategoryName}
                          onChange={(e) => setEditCustomCategoryName(e.target.value)}
                          placeholder="e.g. Art, Drawing, Essay Writing, etc."
                          style={{ width: "100%" }}
                        />
                      </div>
                      <button
                        type="button"
                        className="admin-sc-btn admin-sc-btn-primary"
                        onClick={() => {
                          const val = editCustomCategoryName.trim();
                          if (val) {
                            if (!categories.includes(val)) {
                              setCategories([...categories, val]);
                            }
                            setEditingComp({ ...editingComp, category: val });
                            setIsEditingCustomCategory(false);
                            setEditCustomCategoryName("");
                          } else {
                            alert("Please enter a valid category name.");
                          }
                        }}
                        style={{ padding: "10px 16px", height: "42px", display: "flex", alignItems: "center", justifyContent: "center" }}
                      >
                        Add
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <label className="admin-sc-field-label">
                    Competition ID (Read Only)
                  </label>
                  <input
                    disabled
                    type="text"
                    value={editingComp.competitionId}
                    className="admin-sc-id-input"
                    style={{
                      width: "100%",
                      padding: 11,
                      borderRadius: 8,
                      border: "1px solid var(--sc-border)",
                      backgroundColor: "var(--sc-paper)",
                      color: "var(--sc-slate)",
                    }}
                  />
                </div>

                <div>
                  <label className="admin-sc-field-label">
                    Competition Title *
                  </label>
                  <input
                    className="admin-sc-input"
                    required
                    type="text"
                    value={editingComp.title}
                    onChange={(e) =>
                      setEditingComp({ ...editingComp, title: e.target.value })
                    }
                    style={{ width: "100%" }}
                  />
                </div>

                <div>
                  <label className="admin-sc-field-label">Description</label>
                  <textarea
                    className="admin-sc-textarea"
                    rows="3"
                    value={editingComp.description}
                    onChange={(e) =>
                      setEditingComp({
                        ...editingComp,
                        description: e.target.value,
                      })
                    }
                    style={{ width: "100%" }}
                  />
                </div>

                {/* EDIT PARTICIPATING SCHOOLS SECTION WITH MASTER LIST DROPDOWN */}
                <div className="admin-sc-form-section">
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 10,
                    }}
                  >
                    <label
                      style={{
                        display: "block",
                        fontSize: 14,
                        fontWeight: 700,
                        color: "var(--sc-ink)",
                      }}
                    >
                      Participating Schools
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowSchoolModal(true)}
                      style={{
                        fontSize: 12,
                        color: "var(--sc-navy)",
                        fontWeight: 700,
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                    >
                      + Manage / Add Schools
                    </button>
                  </div>

                  {(() => {
                    const availableSchools = masterSchools.filter(
                      (sch) => !schoolList.includes(sch.name),
                    );
                    return (
                      <select
                        className="admin-sc-filter-select"
                        style={{ width: "100%", backgroundColor: "#fff" }}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "ADD_ALL") {
                            const allNames = masterSchools.map((s) => s.name);
                            setSchoolList(
                              Array.from(new Set([...schoolList, ...allNames])),
                            );
                          } else if (val && !schoolList.includes(val)) {
                            setSchoolList([...schoolList, val]);
                          }
                        }}
                        value=""
                      >
                        <option value="">
                          -- Add School ({availableSchools.length} Remaining /
                          Available) --
                        </option>
                        {availableSchools.length > 0 && (
                          <option
                            value="ADD_ALL"
                            style={{
                              fontWeight: 700,
                              color: "var(--sc-green)",
                            }}
                          >
                            ➕ Add All Schools ({availableSchools.length}{" "}
                            Schools)
                          </option>
                        )}
                        {availableSchools.map((sch) => (
                          <option key={sch.id} value={sch.name}>
                            {sch.name}
                          </option>
                        ))}
                      </select>
                    );
                  })()}

                  {/* School List Display */}
                  {schoolList.length > 0 ? (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 8,
                        marginTop: 12,
                      }}
                    >
                      {schoolList.map((s, idx) => (
                        <span key={idx} className="admin-sc-chip">
                          {s}
                          <button
                            type="button"
                            className="admin-sc-chip-remove"
                            onClick={() => handleRemoveSchool(s)}
                          >
                            ✕
                          </button>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span
                      style={{
                        fontSize: 12,
                        color: "var(--sc-slate)",
                        marginTop: 6,
                        display: "block",
                      }}
                    >
                      No participating schools selected.
                    </span>
                  )}
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 16,
                  }}
                >
                  <div>
                    <label className="admin-sc-field-label">
                      Start Date (Fixed)
                    </label>
                    <input
                      disabled
                      className="admin-sc-input"
                      type="date"
                      value={editingComp.startDate}
                      style={{
                        width: "100%",
                        backgroundColor: "var(--sc-paper)",
                        color: "var(--sc-slate)",
                        cursor: "not-allowed",
                      }}
                    />
                  </div>
                  <div>
                    <label className="admin-sc-field-label">
                      End Date *
                    </label>
                    <input
                      className="admin-sc-input"
                      type="date"
                      value={editingComp.endDate}
                      onChange={(e) => setEditingComp({ ...editingComp, endDate: e.target.value })}
                      style={{
                        width: "100%",
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label className="admin-sc-field-label">
                    Single Shared School Verification Code *
                  </label>
                  <input
                    className="admin-sc-input admin-sc-code-input"
                    required
                    type="text"
                    value={editingComp.verificationCode}
                    onChange={(e) =>
                      setEditingComp({
                        ...editingComp,
                        verificationCode: e.target.value,
                      })
                    }
                    style={{ width: "100%" }}
                  />
                </div>

                <div>
                  <label className="admin-sc-field-label">
                    Winner Announcement Mode
                  </label>
                  <select
                    className="admin-sc-filter-select"
                    value={editingComp.winnerAnnouncementMode}
                    onChange={(e) =>
                      setEditingComp({
                        ...editingComp,
                        winnerAnnouncementMode: e.target.value,
                      })
                    }
                    style={{ width: "100%", backgroundColor: "#fff" }}
                  >
                    <option value="AUTOMATIC">
                      AUTOMATIC (Auto-rank top 3)
                    </option>
                    <option value="MANUAL">
                      MANUAL (Admin manual trigger)
                    </option>
                  </select>
                </div>

                <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                  <button
                    type="button"
                    className="admin-sc-btn admin-sc-btn-ghost"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingComp(null);
                      setSchoolList([]);
                      setIsEditingCustomCategory(false);
                      setEditCustomCategoryName("");
                    }}
                    style={{ flex: 1, padding: 13 }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="admin-sc-btn admin-sc-btn-accent"
                    style={{ flex: 1, padding: 13 }}
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}


        {/* MASTER SCHOOL MANAGEMENT MODAL */}
        {showSchoolModal && (
          <div className="admin-sc-modal-overlay" style={{ zIndex: 1100 }}>
            <div
              className="admin-sc-modal accent-green hide-scrollbar"
              style={{ maxWidth: 900 }}
            >
              <button
                type="button"
                className="rotate-close-btn"
                onClick={() => {
                  setShowSchoolModal(false);
                  setEditingSchool(null);
                  setMasterSchoolInput({ name: "", address: "", code: "" });
                }}
                title="Close Modal"
                style={{
                  position: "sticky",
                  top: -18,
                  float: "right",
                  marginTop: -20,
                  marginRight: -24,
                  zIndex: 20,
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  color: "#ffffff",
                  border: "2px solid #ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 18,
                  fontWeight: 700,
                  cursor: "pointer",
                  boxShadow: "0 4px 12px rgba(20,29,51,0.3)",
                }}
              >
                ✕
              </button>

              <h3 className="admin-sc-modal-title">
                🏫 Central Schools Management
              </h3>

              {/* Input Mode Selector */}
              <div className="admin-sc-segmented">
                <button
                  type="button"
                  className={`admin-sc-segmented-btn ${masterSchoolInputMode === "MANUAL" ? "active" : ""}`}
                  onClick={() => setMasterSchoolInputMode("MANUAL")}
                >
                  + Add / Edit School
                </button>
                <button
                  type="button"
                  className={`admin-sc-segmented-btn ${masterSchoolInputMode === "FILE" ? "active" : ""}`}
                  onClick={() => setMasterSchoolInputMode("FILE")}
                >
                  📁 Bulk Upload
                </button>
              </div>

              {/* Single School Form */}
              {masterSchoolInputMode === "MANUAL" && (
                <form
                  onSubmit={handleAddMasterSchoolSingle}
                  className="admin-sc-form-section"
                  style={{ marginBottom: 20 }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "2fr 1fr",
                      gap: 12,
                      marginBottom: 12,
                    }}
                  >
                    <div>
                      <label className="admin-sc-field-label">
                        School Name *
                      </label>
                      <input
                        className="admin-sc-input"
                        required
                        type="text"
                        value={masterSchoolInput.name}
                        onChange={(e) =>
                          setMasterSchoolInput({
                            ...masterSchoolInput,
                            name: e.target.value,
                          })
                        }
                        placeholder="Enter exact school name"
                        style={{ width: "100%" }}
                      />
                    </div>
                    <div>
                      <label className="admin-sc-field-label">
                        School Code (Optional)
                      </label>
                      <input
                        className="admin-sc-input admin-sc-code-input"
                        type="text"
                        value={masterSchoolInput.code}
                        onChange={(e) =>
                          setMasterSchoolInput({
                            ...masterSchoolInput,
                            code: e.target.value,
                          })
                        }
                        placeholder="e.g. SCH-01"
                        style={{ width: "100%" }}
                      />
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 10 }}>
                    <button
                      type="submit"
                      className="admin-sc-btn admin-sc-btn-success"
                    >
                      {editingSchool
                        ? "✓ Update School"
                        : "+ Add School to Master List"}
                    </button>
                    {editingSchool && (
                      <button
                        type="button"
                        className="admin-sc-btn admin-sc-btn-ghost"
                        onClick={() => {
                          setEditingSchool(null);
                          setMasterSchoolInput({
                            name: "",
                            address: "",
                            code: "",
                          });
                        }}
                        style={{ padding: "11px 18px" }}
                      >
                        Cancel Edit
                      </button>
                    )}
                  </div>
                </form>
              )}

              {/* Bulk XLS Upload Mode */}
              {masterSchoolInputMode === "FILE" && (
                <div
                  className="admin-sc-form-section"
                  style={{ marginBottom: 20 }}
                >
                  <label
                    className="admin-sc-field-label"
                    style={{
                      textTransform: "none",
                      letterSpacing: "normal",
                      fontSize: 13,
                    }}
                  >
                    Select Excel / CSV / Doc File to Bulk Add Schools
                  </label>
                  <div
                    style={{ display: "flex", gap: 10, alignItems: "center" }}
                  >
                    <input
                      type="file"
                      accept=".xlsx, .xls, .csv, .pdf, .doc, .docx"
                      onChange={handleMasterFileChange}
                      style={{
                        flex: 1,
                        padding: 10,
                        backgroundColor: "#fff",
                        borderRadius: 8,
                        border: "1px solid var(--sc-border-strong)",
                      }}
                    />
                    <button
                      type="button"
                      className="admin-sc-btn admin-sc-btn-primary"
                      onClick={handleProcessMasterFileUpload}
                      style={{ padding: "11px 24px", whiteSpace: "nowrap" }}
                    >
                      📤 Upload &amp; Process
                    </button>
                  </div>
                  {selectedBulkFile && (
                    <span
                      style={{
                        fontSize: 12,
                        color: "var(--sc-green-text)",
                        fontWeight: 600,
                        display: "block",
                        marginTop: 8,
                      }}
                    >
                      ✓ Selected File: {selectedBulkFile.name} (Click "Upload
                      &amp; Process" above to add schools)
                    </span>
                  )}
                  <span
                    style={{
                      fontSize: 12,
                      color: "var(--sc-slate)",
                      marginTop: 8,
                      display: "block",
                    }}
                  >
                    All clean school names from the uploaded file will be added
                    directly into the Central Schools Database.
                  </span>
                </div>
              )}

              {/* Master Schools Table */}
              <h4
                style={{
                  fontFamily: "var(--sc-font-display)",
                  fontSize: 16,
                  fontWeight: 600,
                  marginBottom: 14,
                  color: "var(--sc-ink)",
                }}
              >
                Master Schools Database ({masterSchools.length} Schools)
              </h4>
              {masterSchools.length === 0 ? (
                <p className="admin-sc-empty-note" style={{ padding: 0 }}>
                  No schools in master list. Add school manually or upload Excel
                  file above.
                </p>
              ) : (
                <div
                  className="admin-sc-scroll-thin"
                  style={{
                    maxHeight: 300,
                    overflowY: "auto",
                    border: "1px solid var(--sc-border)",
                    borderRadius: 10,
                  }}
                >
                  <table className="admin-sc-table">
                    <thead>
                      <tr style={{ position: "sticky", top: 0 }}>
                        <th>#</th>
                        <th>School Name</th>
                        <th>Code</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {masterSchools.map((sch, idx) => (
                        <tr key={sch.id}>
                          <td
                            style={{
                              fontWeight: 600,
                              color: "var(--sc-slate)",
                            }}
                          >
                            {idx + 1}
                          </td>
                          <td
                            style={{ fontWeight: 700, color: "var(--sc-ink)" }}
                          >
                            {sch.name}
                          </td>
                          <td
                            className="admin-sc-stamp"
                            style={{ color: "var(--sc-navy)" }}
                          >
                            {sch.code || "-"}
                          </td>
                          <td style={{ display: "flex", gap: 8 }}>
                            <button
                              className="admin-sc-btn admin-sc-btn-warning"
                              onClick={() => {
                                setEditingSchool(sch);
                                setMasterSchoolInput({
                                  name: sch.name,
                                  address: sch.address || "",
                                  code: sch.code || "",
                                });
                                setMasterSchoolInputMode("MANUAL");
                              }}
                              style={{ fontSize: 12 }}
                            >
                              ✏️ Edit
                            </button>
                            <button
                              className="admin-sc-btn admin-sc-btn-danger"
                              onClick={() =>
                                handleDeleteMasterSchool(sch.id, sch.name)
                              }
                              style={{ fontSize: 12 }}
                            >
                              🗑️ Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
        {/* PRIZE CEREMONY VIDEO MODAL */}
        {showPrizeCeremonyModal && (
          <div className="admin-sc-modal-overlay" style={{ zIndex: 1150 }}>
            <div
              className="admin-sc-modal accent-amber hide-scrollbar"
              style={{ maxWidth: 850 }}
            >
              <button
                type="button"
                className="rotate-close-btn"
                onClick={handleClosePrizeCeremonyModal}
                title="Close Modal"
                style={{
                  position: "sticky",
                  top: -18,
                  float: "right",
                  marginTop: -20,
                  marginRight: -24,
                  zIndex: 20,
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  color: "#ffffff",
                  border: "2px solid #ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 18,
                  fontWeight: 700,
                  cursor: "pointer",
                  boxShadow: "0 4px 12px rgba(20,29,51,0.3)",
                }}
              >
                ✕
              </button>

              <h3 className="admin-sc-modal-title">
                {editingPrizeVideoId ? "✏️ Edit Prize Ceremony Video" : "🎬 Add Prize Ceremony Video"}
              </h3>
                <form
                  onSubmit={handleSubmitPrizeVideo}
                  style={{ display: "flex", flexDirection: "column", gap: 16 }}
                >
                  <div>
                    <label className="admin-sc-field-label">
                      Competition ID *
                    </label>
                    <input
                      className="admin-sc-input"
                      required
                      type="text"
                      value={prizeVideoForm.competitionId}
                      onChange={(e) =>
                        setPrizeVideoForm({
                          ...prizeVideoForm,
                          competitionId: e.target.value,
                        })
                      }
                      placeholder="Enter Competition ID (e.g. COMP-2025-001)"
                      style={{ width: "100%", backgroundColor: "#fff" }}
                    />
                  </div>

                  <div>
                    <label className="admin-sc-field-label">
                      Competition Name *
                    </label>
                    <input
                      className="admin-sc-input"
                      required
                      type="text"
                      value={prizeVideoForm.competitionName}
                      onChange={(e) =>
                        setPrizeVideoForm({
                          ...prizeVideoForm,
                          competitionName: e.target.value,
                        })
                      }
                      placeholder="Enter Competition Name"
                      style={{
                        width: "100%",
                        backgroundColor: "#fff",
                      }}
                    />
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      gap: 16,
                    }}
                  >
                    <div>
                      <label className="admin-sc-field-label">Competition Type / Category *</label>
                      <select
                        className="admin-sc-filter-select"
                        value={isCreatingPrizeCustomCategory ? "ADD_NEW" : (prizeVideoForm.category || "")}
                        onChange={(e) => {
                          if (e.target.value === "ADD_NEW") {
                            setIsCreatingPrizeCustomCategory(true);
                          } else {
                            setIsCreatingPrizeCustomCategory(false);
                            setPrizeVideoForm({
                              ...prizeVideoForm,
                              category: e.target.value,
                            });
                          }
                        }}
                        style={{ width: "100%", backgroundColor: "#fff" }}
                      >
                        <option value="">-- Select Category --</option>
                        {categories.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat}
                          </option>
                        ))}
                        <option value="ADD_NEW">+ Add New Category</option>
                      </select>

                      {isCreatingPrizeCustomCategory && (
                        <div style={{ marginTop: "10px", display: "flex", gap: "8px", alignItems: "flex-end" }}>
                          <div style={{ flex: 1 }}>
                            <label className="admin-sc-field-label">New Category Name *</label>
                            <input
                              type="text"
                              className="admin-sc-input"
                              value={prizeCustomCategoryName}
                              onChange={(e) => setPrizeCustomCategoryName(e.target.value)}
                              placeholder="e.g. Art, Quiz, Essay, etc."
                              style={{ width: "100%" }}
                            />
                          </div>
                          <button
                            type="button"
                            className="admin-sc-btn admin-sc-btn-primary"
                            onClick={() => {
                              const val = prizeCustomCategoryName.trim();
                              if (val) {
                                if (!categories.includes(val)) {
                                  setCategories([...categories, val]);
                                }
                                setPrizeVideoForm({
                                  ...prizeVideoForm,
                                  category: val,
                                });
                                setIsCreatingPrizeCustomCategory(false);
                                setPrizeCustomCategoryName("");
                              }
                            }}
                          >
                            Add
                          </button>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="admin-sc-field-label">Start Date</label>
                      <input
                        className="admin-sc-input"
                        type="date"
                        value={prizeVideoForm.startDate || ""}
                        onChange={(e) =>
                          setPrizeVideoForm({
                            ...prizeVideoForm,
                            startDate: e.target.value,
                          })
                        }
                        style={{
                          width: "100%",
                          backgroundColor: "#fff",
                        }}
                      />
                    </div>
                    <div>
                      <label className="admin-sc-field-label">End Date</label>
                      <input
                        className="admin-sc-input"
                        type="date"
                        value={prizeVideoForm.endDate || ""}
                        onChange={(e) =>
                          setPrizeVideoForm({
                            ...prizeVideoForm,
                            endDate: e.target.value,
                          })
                        }
                        style={{
                          width: "100%",
                          backgroundColor: "#fff",
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="admin-sc-field-label">
                      Video URL * (Only YouTube or Google Drive Link)
                    </label>
                    <input
                      className="admin-sc-input"
                      required
                      type="text"
                      value={prizeVideoForm.videoUrl}
                      onChange={(e) =>
                        setPrizeVideoForm({
                          ...prizeVideoForm,
                          videoUrl: e.target.value,
                        })
                      }
                      placeholder="e.g. https://www.youtube.com/watch?v=... or https://drive.google.com/file/d/.../view"
                      style={{ width: "100%" }}
                    />
                    <span style={{ fontSize: "12px", color: "var(--sc-slate)", marginTop: "4px", display: "block" }}>
                      ℹ️ Video file upload is disabled for Prize Distribution Ceremony. Please enter a valid YouTube or Google Drive link.
                    </span>
                    {renderAdminVideoPreview(prizeVideoForm.videoUrl)}
                  </div>

                  <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                    <button
                      type="button"
                      className="admin-sc-btn admin-sc-btn-ghost"
                      onClick={handleClosePrizeCeremonyModal}
                      style={{ flex: 1, padding: 13 }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="admin-sc-btn admin-sc-btn-accent"
                      style={{ flex: 1, padding: 13 }}
                    >
                      {editingPrizeVideoId ? "✓ Update Video" : "+ Add Video"}
                    </button>
                  </div>
                </form>
            </div>
          </div>
        )}

        {/* PAST COMPETITION VIDEO MODAL */}
        {showPastVideoModal && (() => {
          const isKojoCompetition = Boolean(
            (pastVideoForm.competitionType && pastVideoForm.competitionType.toLowerCase().includes("kojo")) ||
            (pastVideoForm.competitionName && pastVideoForm.competitionName.toLowerCase().includes("kojo"))
          );

          return (
            <div className="admin-sc-modal-overlay" style={{ zIndex: 1150 }}>
              <div
                className="admin-sc-modal accent-amber hide-scrollbar"
                style={{ maxWidth: 850 }}
              >
                <button
                  type="button"
                  className="rotate-close-btn"
                  onClick={handleClosePastVideoModal}
                  title="Close Modal"
                  style={{
                    position: "sticky",
                    top: -18,
                    float: "right",
                    marginTop: -20,
                    marginRight: -24,
                    zIndex: 20,
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    color: "#ffffff",
                    border: "2px solid #ffffff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 18,
                    fontWeight: 700,
                    cursor: "pointer",
                    boxShadow: "0 4px 12px rgba(20,29,51,0.3)",
                  }}
                >
                  ✕
                </button>

                <h3 className="admin-sc-modal-title">
                  {isEditingGroup || editingPrizeVideoId ? "✏️ Edit Past Competition Video" : "📜 Add Past Competition Video"}
                </h3>

                <form
                  onSubmit={handleSubmitPastVideo}
                  style={{ display: "flex", flexDirection: "column", gap: 16 }}
                >
                  <div>
                    <label className="admin-sc-field-label">
                      Competition Name *
                    </label>
                    <input
                      className="admin-sc-input"
                      type="text"
                      required
                      value={pastVideoForm.competitionName}
                      onChange={(e) =>
                        setPastVideoForm({
                          ...pastVideoForm,
                          competitionName: e.target.value,
                        })
                      }
                      placeholder="e.g. Annual Drawing Competition 2024"
                      style={{ width: "100%" }}
                    />
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 16,
                    }}
                  >
                    <div>
                      <label className="admin-sc-field-label">Year *</label>
                      <select
                        className="admin-sc-filter-select"
                        value={pastVideoForm.year}
                        onChange={(e) =>
                          setPastVideoForm({
                            ...pastVideoForm,
                            year: e.target.value,
                          })
                        }
                        style={{ width: "100%", backgroundColor: "#fff" }}
                      >
                        {[2020, 2021, 2022, 2023, 2024, 2025, 2026, 2027].map((yr) => (
                          <option key={yr} value={yr}>
                            {yr}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="admin-sc-field-label">Month *</label>
                      <select
                        className="admin-sc-filter-select"
                        value={pastVideoForm.month}
                        onChange={(e) =>
                          setPastVideoForm({
                            ...pastVideoForm,
                            month: e.target.value,
                          })
                        }
                        style={{ width: "100%", backgroundColor: "#fff" }}
                      >
                        {[
                          "January", "February", "March", "April", "May", "June",
                          "July", "August", "September", "October", "November", "December"
                        ].map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 16,
                    }}
                  >
                    <div>
                      <label className="admin-sc-field-label">Competition Type / Category *</label>
                      <select
                        className="admin-sc-filter-select"
                        value={isCreatingPastCustomCategory ? "ADD_NEW" : pastVideoForm.competitionType}
                        onChange={(e) => {
                          if (e.target.value === "ADD_NEW") {
                            setIsCreatingPastCustomCategory(true);
                          } else {
                            setIsCreatingPastCustomCategory(false);
                            const selectedType = e.target.value;
                            const isKojo = selectedType.toLowerCase().includes("kojo");
                            setPastVideoForm({
                              ...pastVideoForm,
                              competitionType: selectedType,
                              groupCategory: isKojo ? "Group C" : pastVideoForm.groupCategory,
                            });
                          }
                        }}
                        style={{ width: "100%", backgroundColor: "#fff" }}
                      >
                        {categories.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat}
                          </option>
                        ))}
                        <option value="ADD_NEW">+ Add New Category</option>
                      </select>

                      {isCreatingPastCustomCategory && (
                        <div style={{ marginTop: "10px", display: "flex", gap: "8px", alignItems: "flex-end" }}>
                          <div style={{ flex: 1 }}>
                            <label className="admin-sc-field-label">New Category Name *</label>
                            <input
                              type="text"
                              className="admin-sc-input"
                              value={pastCustomCategoryName}
                              onChange={(e) => setPastCustomCategoryName(e.target.value)}
                              placeholder="e.g. Art, Quiz, Essay, etc."
                              style={{ width: "100%" }}
                            />
                          </div>
                          <button
                            type="button"
                            className="admin-sc-btn admin-sc-btn-primary"
                            onClick={() => {
                              const val = pastCustomCategoryName.trim();
                              if (val) {
                                if (!categories.includes(val)) {
                                  setCategories([...categories, val]);
                                }
                                const isKojo = val.toLowerCase().includes("kojo");
                                setPastVideoForm({
                                  ...pastVideoForm,
                                  competitionType: val,
                                  groupCategory: isKojo ? "Group C" : pastVideoForm.groupCategory,
                                });
                                setIsCreatingPastCustomCategory(false);
                                setPastCustomCategoryName("");
                              }
                            }}
                          >
                            Add
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* IF EDITING SINGLE PAST VIDEO */}
                  {editingPrizeVideoId ? (
                    <>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 16,
                        }}
                      >
                        <div>
                          <label className="admin-sc-field-label">Group Category *</label>
                          <select
                            className="admin-sc-filter-select"
                            value={isKojoCompetition ? (pastVideoForm.groupCategory.includes("Group C") ? pastVideoForm.groupCategory : "Group C (Class 6)") : pastVideoForm.groupCategory}
                            onChange={(e) =>
                              setPastVideoForm({
                                ...pastVideoForm,
                                groupCategory: e.target.value,
                              })
                            }
                            style={{ width: "100%", backgroundColor: "#fff" }}
                          >
                            {isKojoCompetition ? (
                              <>
                                <option value="Group C (Class 6)">Group C — Class 6th</option>
                                <option value="Group C (Class 7)">Group C — Class 7th</option>
                                <option value="Group C (Class 8)">Group C — Class 8th</option>
                                <option value="Group C (Class 6-8)">Group C (Class 6-8 Combined)</option>
                              </>
                            ) : (
                              <>
                                <option value="Group A">Group A (Class 1-2)</option>
                                <option value="Group B">Group B (Class 3-5)</option>
                                <option value="Group C">Group C (Class 6-8)</option>
                                <option value="Group D">Group D (Class 9-12)</option>
                              </>
                            )}
                          </select>
                          {isKojoCompetition && (
                            <span style={{ fontSize: "11px", color: "#2563eb", marginTop: "4px", display: "block", fontWeight: "600" }}>
                              ℹ️ Kojo Competition: Individual class entries for Class 6th, 7th, &amp; 8th.
                            </span>
                          )}
                        </div>

                        <div>
                          <label className="admin-sc-field-label">Winner Rank</label>
                          <select
                            className="admin-sc-filter-select"
                            value={pastVideoForm.winnerRank}
                            onChange={(e) =>
                              setPastVideoForm({
                                ...pastVideoForm,
                                winnerRank: e.target.value,
                              })
                            }
                            style={{ width: "100%", backgroundColor: "#fff" }}
                          >
                            <option value="1">🥇 Rank 1 (1st Winner)</option>
                            <option value="2">🥈 Rank 2 (2nd Runner-up)</option>
                            <option value="3">🥉 Rank 3 (3rd Runner-up)</option>
                            <option value="0">General Video (No Rank)</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="admin-sc-field-label">Prize Amount (₹)</label>
                        <input
                          className="admin-sc-input"
                          type="text"
                          value={pastVideoForm.prizeAmount}
                          onChange={(e) =>
                            setPastVideoForm({
                              ...pastVideoForm,
                              prizeAmount: e.target.value,
                            })
                          }
                          placeholder="e.g. ₹5,000 or 5000"
                          style={{ width: "100%" }}
                        />
                      </div>

                      <div>
                        <label className="admin-sc-field-label">
                          Upload Video File (MP4 / WebM / Mov)
                        </label>
                        <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "8px" }}>
                          <input
                            type="file"
                            accept="video/*"
                            disabled={uploadingVideo}
                            onChange={(e) => {
                              if (e.target.files && e.target.files[0]) {
                                handleFileUploadForVideo(e.target.files[0], "PAST");
                              }
                            }}
                            style={{ flex: 1, padding: "8px", background: "#fff", border: "1px solid var(--sc-border)", borderRadius: "8px" }}
                          />
                          {uploadingVideo && (
                            <span style={{ fontSize: "13px", color: "var(--sc-accent)", fontWeight: "600" }}>
                              ⏳ Uploading to S3...
                            </span>
                          )}
                        </div>

                        <label className="admin-sc-field-label">
                          Or Paste Video Link * (YouTube / Google Cloud / MP4 Video)
                        </label>
                        <input
                          className="admin-sc-input"
                          required
                          type="text"
                          value={pastVideoForm.videoUrl}
                          onChange={(e) =>
                            setPastVideoForm({
                              ...pastVideoForm,
                              videoUrl: e.target.value,
                            })
                          }
                          placeholder="Paste YouTube link (https://youtube.com/...) or Google Cloud Link..."
                          style={{ width: "100%" }}
                        />
                        {renderAdminVideoPreview(pastVideoForm.videoUrl)}
                      </div>
                    </>
                  ) : (
                    /* NEW PAST COMPETITION: WINNERS BATCH GRID */
                    <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 16 }}>
                      {/* Notice when Kojo Competition */}
                      {isKojoCompetition && (
                        <div style={{ background: "#eff6ff", border: "1.5px solid #3b82f6", borderRadius: "10px", padding: "12px 16px", display: "flex", alignItems: "center", gap: "12px" }}>
                          <span style={{ fontSize: "24px" }}>📘</span>
                          <div>
                            <div style={{ color: "#1e40af", fontWeight: "800", fontSize: "14px" }}>
                              Kojo Competition &mdash; Individual Class Entry for Group C (Class 6th, 7th, &amp; 8th):
                            </div>
                            <div style={{ color: "#2563eb", fontSize: "12.5px", marginTop: "2px" }}>
                              Each class (<strong>Class 6th, Class 7th, and Class 8th</strong>) has separate winner entries for <strong>Rank 1, 2, 3, and optional Consolation prizes</strong>.
                            </div>
                          </div>
                        </div>
                      )}

                      <div style={{ background: "var(--sc-paper)", padding: "12px 16px", borderRadius: "8px", border: "1px solid var(--sc-border)" }}>
                        <h4 style={{ margin: "0 0 4px 0", color: "var(--sc-navy)", fontSize: "15px", fontWeight: "700" }}>
                          🏆 {isKojoCompetition ? "Winner Entries Grid (Group C — Class 6th, 7th, & 8th Individual Entries)" : "Winner Videos Grid (4 Groups × 3 Ranks = 12 Winner Videos)"}
                        </h4>
                        <p style={{ margin: 0, fontSize: "12px", color: "#64748b" }}>
                          {isKojoCompetition
                            ? "Upload Word (.doc/.docx), PDF (.pdf), TXT (.txt), or Image files for Class 6th, 7th, & 8th rank & consolation winners. (Videos are not allowed)"
                            : "Upload video files or paste video links for each group's 1st, 2nd, and 3rd rank winners."}
                        </p>
                      </div>

                      {(isKojoCompetition
                        ? ["Group C (Class 6)", "Group C (Class 7)", "Group C (Class 8)"]
                        : ["Group A (Class 1-2)", "Group B (Class 3-5)", "Group C (Class 6-8)", "Group D (Class 9-12)"]
                      ).map((groupName) => {
                        const isGroupA = groupName.includes("Group A");
                        const isGroupB = groupName.includes("Group B");
                        const isGroupC = groupName.includes("Group C");
                        
                        const headerBg = isGroupA ? "linear-gradient(135deg, #0284c7, #0369a1)"
                          : isGroupB ? "linear-gradient(135deg, #0d9488, #0f766e)"
                          : isGroupC ? "linear-gradient(135deg, #4f46e5, #4338ca)"
                          : "linear-gradient(135deg, #9333ea, #7e22ce)";
                        
                        const icon = isGroupA ? "🌱" : isGroupB ? "🌿" : isGroupC ? "📘" : "🎓";

                        return (
                          <div key={groupName} style={{ border: "1px solid #e2e8f0", borderRadius: "14px", overflow: "hidden", background: "#fff", boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05)" }}>
                            <div style={{ background: headerBg, padding: "12px 16px", color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                              <h5 style={{ margin: 0, fontSize: "15px", fontWeight: "800", display: "flex", alignItems: "center", gap: "8px" }}>
                                <span>{icon}</span> {groupName} Winners Card
                              </h5>
                              <span style={{ background: "rgba(255,255,255,0.2)", fontSize: "11px", padding: "3px 10px", borderRadius: "12px", fontWeight: "700" }}>
                                3 Winners (Rank 1 - 3)
                              </span>
                            </div>

                            <div style={{ padding: "14px", display: "flex", flexDirection: "column", gap: "10px" }}>
                            {/* MAIN TOP 3 RANK WINNERS */}
                            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                              {batchWinners
                                .map((w, idx) => ({ ...w, originalIndex: idx }))
                                .filter((w) => w.groupCategory === groupName && !w.isConsolation)
                                .map((item) => (
                                  <div
                                    key={item.originalIndex}
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: "10px",
                                      background: "#f8fafc",
                                      padding: "12px 14px",
                                      borderRadius: "10px",
                                      border: "1px solid #e2e8f0",
                                      boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
                                    }}
                                  >
                                    {/* ROW 1: Identifying info */}
                                    <div style={{ display: "flex", gap: "12px", alignItems: "flex-start", flexWrap: "wrap" }}>
                                      <div style={{ minWidth: "180px", fontWeight: "700", fontSize: "13px", color: "var(--sc-navy)", paddingTop: "6px" }}>
                                        {item.label}
                                      </div>
                                      <div style={{ flex: 1, minWidth: "200px" }}>
                                        {(() => {
                                          const namesArray = (item.studentName || "").split(",").map(s => s.trimStart());
                                          const currentStudents = namesArray.length > 0 ? namesArray : [""];

                                          return (
                                            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                              {currentStudents.map((sName, sIdx) => (
                                                <div key={sIdx} style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                                                  {currentStudents.length > 1 && (
                                                    <span style={{ fontSize: "10px", color: "#64748b", fontWeight: "700" }}>#{sIdx + 1}</span>
                                                  )}
                                                  <input
                                                    type="text"
                                                    className="admin-sc-input"
                                                    placeholder={isKojoCompetition ? `Student ${sIdx + 1} Name` : "Student Name"}
                                                    value={sName}
                                                    onChange={(e) => {
                                                      const updated = [...batchWinners];
                                                      const sUpdated = [...currentStudents];
                                                      sUpdated[sIdx] = e.target.value;
                                                      updated[item.originalIndex].studentName = sUpdated.join(", ");
                                                      setBatchWinners(updated);
                                                    }}
                                                    style={{ flex: 1, padding: "6px 10px", fontSize: "12px" }}
                                                  />
                                                  {currentStudents.length > 1 && (
                                                    <button
                                                      type="button"
                                                      onClick={() => {
                                                        const updated = [...batchWinners];
                                                        const sUpdated = currentStudents.filter((_, i) => i !== sIdx);
                                                        updated[item.originalIndex].studentName = sUpdated.join(", ");
                                                        setBatchWinners(updated);
                                                      }}
                                                      style={{
                                                        background: "#fee2e2",
                                                        color: "#dc2626",
                                                        border: "none",
                                                        borderRadius: "4px",
                                                        padding: "2px 6px",
                                                        fontSize: "11px",
                                                        fontWeight: "700",
                                                        cursor: "pointer"
                                                      }}
                                                      title="Remove Student"
                                                    >
                                                      ✕
                                                    </button>
                                                  )}
                                                </div>
                                              ))}
                                              {currentStudents.length < 5 && (
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    const updated = [...batchWinners];
                                                    const sUpdated = [...currentStudents, ""];
                                                    updated[item.originalIndex].studentName = sUpdated.join(", ");
                                                    setBatchWinners(updated);
                                                  }}
                                                  style={{
                                                    padding: "3px 8px",
                                                    background: "#eff6ff",
                                                    color: "#2563eb",
                                                    border: "1px dashed #93c5fd",
                                                    borderRadius: "4px",
                                                    fontSize: "11px",
                                                    fontWeight: "700",
                                                    cursor: "pointer",
                                                    alignSelf: "flex-start",
                                                    marginTop: "2px"
                                                  }}
                                                >
                                                  ➕ Add Student ({currentStudents.length}/5)
                                                </button>
                                              )}
                                            </div>
                                          );
                                        })()}
                                      </div>
                                      <div style={{ width: "90px" }}>
                                        <input
                                          type="text"
                                          className="admin-sc-input"
                                          placeholder="Class"
                                          value={item.studentClass || ""}
                                          onChange={(e) => {
                                            const updated = [...batchWinners];
                                            updated[item.originalIndex].studentClass = e.target.value;
                                            setBatchWinners(updated);
                                          }}
                                          style={{ width: "100%", padding: "6px 10px", fontSize: "12px" }}
                                        />
                                      </div>
                                      <div style={{ width: "110px", display: "flex", flexDirection: "column", gap: "2px" }}>
                                        <input
                                          type="text"
                                          className="admin-sc-input"
                                          placeholder="Roll No"
                                          disabled={Boolean(item.disableRollNumber) || item.rollNumber === "N/A"}
                                          value={(Boolean(item.disableRollNumber) || item.rollNumber === "N/A") ? "N/A" : (item.rollNumber || "")}
                                          onChange={(e) => {
                                            const updated = [...batchWinners];
                                            updated[item.originalIndex].rollNumber = e.target.value;
                                            setBatchWinners(updated);
                                          }}
                                          style={{
                                            width: "100%",
                                            padding: "6px 10px",
                                            fontSize: "12px",
                                            backgroundColor: (Boolean(item.disableRollNumber) || item.rollNumber === "N/A") ? "#f1f5f9" : "#fff",
                                            color: (Boolean(item.disableRollNumber) || item.rollNumber === "N/A") ? "#94a3b8" : "#0f172a"
                                          }}
                                        />
                                        <label style={{ fontSize: "10px", color: "#64748b", cursor: "pointer", display: "flex", alignItems: "center", gap: "3px" }}>
                                          <input
                                            type="checkbox"
                                            checked={Boolean(item.disableRollNumber) || item.rollNumber === "N/A"}
                                            onChange={(e) => {
                                              const isDisabled = e.target.checked;
                                              const updated = [...batchWinners];
                                              updated[item.originalIndex].disableRollNumber = isDisabled;
                                              updated[item.originalIndex].rollNumber = isDisabled ? "N/A" : "";
                                              setBatchWinners(updated);
                                            }}
                                          />
                                          <span>Disable Roll</span>
                                        </label>
                                      </div>
                                      <div style={{ flex: 1, minWidth: "180px" }}>
                                        <input
                                          type="text"
                                          className="admin-sc-input"
                                          placeholder="School Name"
                                          value={item.schoolName || ""}
                                          onChange={(e) => {
                                            const updated = [...batchWinners];
                                            updated[item.originalIndex].schoolName = e.target.value;
                                            setBatchWinners(updated);
                                          }}
                                          style={{ width: "100%", padding: "6px 10px", fontSize: "12px" }}
                                        />
                                      </div>
                                    </div>

                                    {/* ROW 2: Prize & Video info */}
                                    <div style={{ display: "flex", gap: "12px", alignItems: "flex-start", flexWrap: "wrap" }}>
                                      <div style={{ width: "130px" }}>
                                        <input
                                          type="text"
                                          className="admin-sc-input"
                                          placeholder="Prize Amount"
                                          value={item.prizeAmount || ""}
                                          onChange={(e) => {
                                            const updated = [...batchWinners];
                                            updated[item.originalIndex].prizeAmount = e.target.value;
                                            setBatchWinners(updated);
                                          }}
                                          style={{ width: "100%", padding: "6px 10px", fontSize: "12px" }}
                                        />
                                      </div>

                                      <div style={{ flex: 1, minWidth: "200px", display: "flex", gap: "6px" }}>
                                        <input
                                          type="file"
                                          accept={isKojoCompetition ? ".doc,.docx,.pdf,.txt,image/*" : "video/*"}
                                          disabled={uploadingVideo}
                                          onChange={(e) => {
                                            if (e.target.files && e.target.files[0]) {
                                              const f = e.target.files[0];
                                              if (isKojoCompetition && (f.type.startsWith("video/") || f.name.match(/\.(mp4|mov|avi|mkv|3gp|webm|wmv|flv)$/i))) {
                                                setMsg("⚠️ Video files are NOT allowed for Kojo Competition! Please select a Word (.doc/.docx), PDF (.pdf), TXT (.txt), or Image file.");
                                                e.target.value = "";
                                                return;
                                              }
                                              handleBatchFileUpload(f, item.originalIndex);
                                            }
                                          }}
                                          style={{ flex: 1, padding: "4px 8px", fontSize: "12px", background: "#fff", border: "1px solid #cbd5e1", borderRadius: "6px" }}
                                        />
                                      </div>

                                      <div style={{ flex: 1, minWidth: "200px" }}>
                                        <input
                                          type="text"
                                          className="admin-sc-input"
                                          placeholder={isKojoCompetition ? "Or paste Word, PDF, TXT or Image link / Drive URL..." : "Or paste video URL..."}
                                          value={item.videoUrl}
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            if (isKojoCompetition && (val.includes("youtube.com") || val.includes("youtu.be") || val.match(/\.(mp4|mov|avi|mkv|3gp|webm|wmv|flv)(\?|$)/i))) {
                                              setMsg("⚠️ Video links are NOT allowed for Kojo Competition! Please enter a link to a Word document, PDF, TXT file, or Image.");
                                              return;
                                            }
                                            const updated = [...batchWinners];
                                            updated[item.originalIndex].videoUrl = val;
                                            setBatchWinners(updated);
                                          }}
                                          style={{ width: "100%", padding: "6px 10px", fontSize: "12px" }}
                                        />
                                      </div>

                                      {item.videoUrl && (
                                        <div style={{ minWidth: "100px" }}>
                                          {renderAdminVideoPreview(item.videoUrl)}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                            </div>

                            {/* MODERN CONSOLATION PRIZE SECTION */}
                            <div
                              style={{
                                marginTop: "14px",
                                background: includeConsolation[groupName] ? "#fffdf5" : "#f8fafc",
                                border: includeConsolation[groupName] ? "1.5px solid #f59e0b" : "1.5px dashed #cbd5e1",
                                borderRadius: "12px",
                                padding: "14px 16px",
                                transition: "all 0.25s ease-in-out",
                              }}
                            >
                              {/* TOGGLE HEADER BAR */}
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  flexWrap: "wrap",
                                  gap: "12px",
                                  cursor: "pointer",
                                  userSelect: "none",
                                }}
                                onClick={() =>
                                  setIncludeConsolation({
                                    ...includeConsolation,
                                    [groupName]: !includeConsolation[groupName],
                                  })
                                }
                              >
                                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                  <div
                                    style={{
                                      width: "40px",
                                      height: "40px",
                                      borderRadius: "10px",
                                      background: includeConsolation[groupName] ? "linear-gradient(135deg, #fef3c7, #fde68a)" : "#f1f5f9",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontSize: "20px",
                                      border: includeConsolation[groupName] ? "1px solid #f59e0b" : "1px solid #cbd5e1",
                                      boxShadow: includeConsolation[groupName] ? "0 2px 6px rgba(245, 158, 11, 0.2)" : "none",
                                    }}
                                  >
                                    🎁
                                  </div>
                                  <div>
                                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                                      <span style={{ fontWeight: "800", fontSize: "14.5px", color: includeConsolation[groupName] ? "#92400e" : "#1e293b" }}>
                                        Add Consolation Prize Winners?
                                      </span>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          fontWeight: "800",
                                          padding: "3px 10px",
                                          borderRadius: "20px",
                                          background: includeConsolation[groupName] ? "#f59e0b" : "#e2e8f0",
                                          color: includeConsolation[groupName] ? "#ffffff" : "#64748b",
                                          letterSpacing: "0.2px",
                                        }}
                                      >
                                        Optional — 3 Winners
                                      </span>
                                    </div>
                                    <p style={{ margin: "3px 0 0 0", fontSize: "12px", color: includeConsolation[groupName] ? "#b45309" : "#64748b" }}>
                                      {includeConsolation[groupName]
                                        ? "✓ Consolation prizes active! Add student names, details, and videos below."
                                        : "Click here to add 3 extra consolation prize winners (Consolation #1, #2, #3)."}
                                    </p>
                                  </div>
                                </div>

                                {/* Custom Animated Toggle Switch */}
                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                  <span style={{ fontSize: "12px", fontWeight: "700", color: includeConsolation[groupName] ? "#d97706" : "#94a3b8" }}>
                                    {includeConsolation[groupName] ? "ENABLED" : "OFF"}
                                  </span>
                                  <div
                                    style={{
                                      position: "relative",
                                      width: "48px",
                                      height: "26px",
                                      borderRadius: "13px",
                                      backgroundColor: includeConsolation[groupName] ? "#f59e0b" : "#cbd5e1",
                                      transition: "background-color 0.25s ease",
                                    }}
                                  >
                                    <div
                                      style={{
                                        position: "absolute",
                                        top: "3px",
                                        left: includeConsolation[groupName] ? "25px" : "3px",
                                        width: "20px",
                                        height: "20px",
                                        borderRadius: "50%",
                                        backgroundColor: "#ffffff",
                                        boxShadow: "0 2px 5px rgba(0,0,0,0.25)",
                                        transition: "left 0.25s ease",
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* IF ACTIVE: RENDER THE 3 CONSOLATION WINNER CARDS */}
                              {includeConsolation[groupName] && (
                                <div
                                  style={{
                                    marginTop: "14px",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "12px",
                                    borderTop: "1.5px dashed #fde68a",
                                    paddingTop: "14px",
                                  }}
                                >
                                  {/* WEBSITE VISIBILITY TOGGLE CARD */}
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      background: consolationShowOnWeb[groupName] ? "#f0fdf4" : "#fef2f2",
                                      border: consolationShowOnWeb[groupName] ? "1.5px solid #22c55e" : "1.5px solid #ef4444",
                                      borderRadius: "10px",
                                      padding: "12px 16px",
                                      cursor: "pointer",
                                      userSelect: "none",
                                      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                                      transition: "all 0.2s ease"
                                    }}
                                    onClick={() =>
                                      setConsolationShowOnWeb({
                                        ...consolationShowOnWeb,
                                        [groupName]: !consolationShowOnWeb[groupName],
                                      })
                                    }
                                  >
                                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                      <span style={{ fontSize: "20px" }}>
                                        {consolationShowOnWeb[groupName] ? "🌐" : "🔒"}
                                      </span>
                                      <div>
                                        <div style={{ fontWeight: "800", fontSize: "13.5px", color: consolationShowOnWeb[groupName] ? "#15803d" : "#b91c1c" }}>
                                          Show Consolation Winners on Public Website?
                                        </div>
                                        <div style={{ fontSize: "11.5px", color: consolationShowOnWeb[groupName] ? "#166534" : "#991b1b", marginTop: "2px" }}>
                                          {consolationShowOnWeb[groupName]
                                            ? "✓ Status: VISIBLE ON WEBSITE — Users will see these consolation winners on the public website."
                                            : "✗ Status: HIDDEN FROM WEBSITE — Winners will be saved in Admin records only, NOT visible on the public website."}
                                        </div>
                                      </div>
                                    </div>

                                    {/* Toggle Pill */}
                                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                      <span style={{ fontSize: "11px", fontWeight: "800", color: consolationShowOnWeb[groupName] ? "#16a34a" : "#dc2626" }}>
                                        {consolationShowOnWeb[groupName] ? "VISIBLE" : "HIDDEN"}
                                      </span>
                                      <div
                                        style={{
                                          position: "relative",
                                          width: "44px",
                                          height: "24px",
                                          borderRadius: "12px",
                                          backgroundColor: consolationShowOnWeb[groupName] ? "#22c55e" : "#ef4444",
                                          transition: "background-color 0.25s ease",
                                        }}
                                      >
                                        <div
                                          style={{
                                            position: "absolute",
                                            top: "3px",
                                            left: consolationShowOnWeb[groupName] ? "23px" : "3px",
                                            width: "18px",
                                            height: "18px",
                                            borderRadius: "50%",
                                            backgroundColor: "#ffffff",
                                            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                            transition: "left 0.25s ease",
                                          }}
                                        />
                                      </div>
                                    </div>
                                  </div>

                                  {batchWinners
                                    .map((w, idx) => ({ ...w, originalIndex: idx }))
                                    .filter((w) => w.groupCategory === groupName && w.isConsolation)
                                    .map((item) => (
                                      <div
                                        key={item.originalIndex}
                                        style={{
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: "10px",
                                          background: "#ffffff",
                                          padding: "12px 14px",
                                          borderRadius: "10px",
                                          border: "1.5px solid #fde68a",
                                          boxShadow: "0 2px 8px rgba(245, 158, 11, 0.08)",
                                        }}
                                      >
                                        {/* ROW 1: Identifying info */}
                                        <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
                                          <div
                                            style={{
                                              minWidth: "180px",
                                              fontWeight: "800",
                                              fontSize: "13px",
                                              color: "#b45309",
                                              paddingTop: "6px",
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "6px",
                                            }}
                                          >
                                            <span style={{ fontSize: "16px" }}>🎁</span>
                                            <span>{item.label.replace(/^🎁\s*/, "")}</span>
                                          </div>
                                          <div style={{ flex: 1, minWidth: "200px" }}>
                                            {(() => {
                                              const namesArray = (item.studentName || "").split(",").map(s => s.trimStart());
                                              const currentStudents = namesArray.length > 0 ? namesArray : [""];

                                              return (
                                                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                                  {currentStudents.map((sName, sIdx) => (
                                                    <div key={sIdx} style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                                                      {currentStudents.length > 1 && (
                                                        <span style={{ fontSize: "10px", color: "#64748b", fontWeight: "700" }}>#{sIdx + 1}</span>
                                                      )}
                                                      <input
                                                        type="text"
                                                        className="admin-sc-input"
                                                        placeholder={isKojoCompetition ? `Student ${sIdx + 1} Name` : "Student Name"}
                                                        value={sName}
                                                        onChange={(e) => {
                                                          const updated = [...batchWinners];
                                                          const sUpdated = [...currentStudents];
                                                          sUpdated[sIdx] = e.target.value;
                                                          updated[item.originalIndex].studentName = sUpdated.join(", ");
                                                          setBatchWinners(updated);
                                                        }}
                                                        style={{ flex: 1, padding: "6px 10px", fontSize: "12px" }}
                                                      />
                                                      {currentStudents.length > 1 && (
                                                        <button
                                                          type="button"
                                                          onClick={() => {
                                                            const updated = [...batchWinners];
                                                            const sUpdated = currentStudents.filter((_, i) => i !== sIdx);
                                                            updated[item.originalIndex].studentName = sUpdated.join(", ");
                                                            setBatchWinners(updated);
                                                          }}
                                                          style={{
                                                            background: "#fee2e2",
                                                            color: "#dc2626",
                                                            border: "none",
                                                            borderRadius: "4px",
                                                            padding: "2px 6px",
                                                            fontSize: "11px",
                                                            fontWeight: "700",
                                                            cursor: "pointer"
                                                          }}
                                                          title="Remove Student"
                                                        >
                                                          ✕
                                                        </button>
                                                      )}
                                                    </div>
                                                  ))}
                                                  {currentStudents.length < 5 && (
                                                    <button
                                                      type="button"
                                                      onClick={() => {
                                                        const updated = [...batchWinners];
                                                        const sUpdated = [...currentStudents, ""];
                                                        updated[item.originalIndex].studentName = sUpdated.join(", ");
                                                        setBatchWinners(updated);
                                                      }}
                                                      style={{
                                                        padding: "3px 8px",
                                                        background: "#eff6ff",
                                                        color: "#2563eb",
                                                        border: "1px dashed #93c5fd",
                                                        borderRadius: "4px",
                                                        fontSize: "11px",
                                                        fontWeight: "700",
                                                        cursor: "pointer",
                                                        alignSelf: "flex-start",
                                                        marginTop: "2px"
                                                      }}
                                                    >
                                                      ➕ Add Student ({currentStudents.length}/5)
                                                    </button>
                                                  )}
                                                </div>
                                              );
                                            })()}
                                          </div>
                                          <div style={{ width: "90px" }}>
                                            <input
                                              type="text"
                                              className="admin-sc-input"
                                              placeholder="Class"
                                              value={item.studentClass || ""}
                                              onChange={(e) => {
                                                const updated = [...batchWinners];
                                                updated[item.originalIndex].studentClass = e.target.value;
                                                setBatchWinners(updated);
                                              }}
                                              style={{ width: "100%", padding: "6px 10px", fontSize: "12px" }}
                                            />
                                          </div>
                                          <div style={{ width: "110px", display: "flex", flexDirection: "column", gap: "2px" }}>
                                            <input
                                              type="text"
                                              className="admin-sc-input"
                                              placeholder="Roll No"
                                              disabled={Boolean(item.disableRollNumber) || item.rollNumber === "N/A"}
                                              value={(Boolean(item.disableRollNumber) || item.rollNumber === "N/A") ? "N/A" : (item.rollNumber || "")}
                                              onChange={(e) => {
                                                const updated = [...batchWinners];
                                                updated[item.originalIndex].rollNumber = e.target.value;
                                                setBatchWinners(updated);
                                              }}
                                              style={{
                                                width: "100%",
                                                padding: "6px 10px",
                                                fontSize: "12px",
                                                backgroundColor: (Boolean(item.disableRollNumber) || item.rollNumber === "N/A") ? "#f1f5f9" : "#fff",
                                                color: (Boolean(item.disableRollNumber) || item.rollNumber === "N/A") ? "#94a3b8" : "#0f172a"
                                              }}
                                            />
                                            <label style={{ fontSize: "10px", color: "#64748b", cursor: "pointer", display: "flex", alignItems: "center", gap: "3px" }}>
                                              <input
                                                type="checkbox"
                                                checked={Boolean(item.disableRollNumber) || item.rollNumber === "N/A"}
                                                onChange={(e) => {
                                                  const isDisabled = e.target.checked;
                                                  const updated = [...batchWinners];
                                                  updated[item.originalIndex].disableRollNumber = isDisabled;
                                                  updated[item.originalIndex].rollNumber = isDisabled ? "N/A" : "";
                                                  setBatchWinners(updated);
                                                }}
                                              />
                                              <span>Disable Roll</span>
                                            </label>
                                          </div>
                                          <div style={{ flex: 1, minWidth: "180px" }}>
                                            <input
                                              type="text"
                                              className="admin-sc-input"
                                              placeholder="School Name"
                                              value={item.schoolName || ""}
                                              onChange={(e) => {
                                                const updated = [...batchWinners];
                                                updated[item.originalIndex].schoolName = e.target.value;
                                                setBatchWinners(updated);
                                              }}
                                              style={{ width: "100%", padding: "6px 10px", fontSize: "12px" }}
                                            />
                                          </div>
                                        </div>

                                        {/* ROW 2: Prize & Video info */}
                                        <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
                                          <div style={{ width: "130px" }}>
                                            <input
                                              type="text"
                                              className="admin-sc-input"
                                              placeholder="Prize Amount"
                                              value={item.prizeAmount || ""}
                                              onChange={(e) => {
                                                const updated = [...batchWinners];
                                                updated[item.originalIndex].prizeAmount = e.target.value;
                                                setBatchWinners(updated);
                                              }}
                                              style={{
                                                width: "100%",
                                                padding: "6px 10px",
                                                fontSize: "12px",
                                                borderColor: "#fde68a",
                                                backgroundColor: "#fffdf5",
                                                fontWeight: "600",
                                                color: "#92400e",
                                              }}
                                            />
                                          </div>

                                          <div style={{ flex: 1, minWidth: "200px", display: "flex", gap: "6px" }}>
                                            <input
                                              type="file"
                                              accept={isKojoCompetition ? ".doc,.docx,.pdf,.txt,image/*" : "video/*"}
                                              disabled={uploadingVideo}
                                              onChange={(e) => {
                                                if (e.target.files && e.target.files[0]) {
                                                  const f = e.target.files[0];
                                                  if (isKojoCompetition && (f.type.startsWith("video/") || f.name.match(/\.(mp4|mov|avi|mkv|3gp|webm|wmv|flv)$/i))) {
                                                    setMsg("⚠️ Video files are NOT allowed for Kojo Competition! Please select a Word (.doc/.docx), PDF (.pdf), TXT (.txt), or Image file.");
                                                    e.target.value = "";
                                                    return;
                                                  }
                                                  handleBatchFileUpload(f, item.originalIndex);
                                                }
                                              }}
                                              style={{ flex: 1, padding: "4px 8px", fontSize: "12px", background: "#fff", border: "1px solid #cbd5e1", borderRadius: "6px" }}
                                            />
                                          </div>

                                          <div style={{ flex: 1, minWidth: "200px" }}>
                                            <input
                                              type="text"
                                              className="admin-sc-input"
                                              placeholder={isKojoCompetition ? "Or paste Word, PDF, TXT or Image link / Drive URL..." : "Or paste video URL..."}
                                              value={item.videoUrl}
                                              onChange={(e) => {
                                                const val = e.target.value;
                                                if (isKojoCompetition && (val.includes("youtube.com") || val.includes("youtu.be") || val.match(/\.(mp4|mov|avi|mkv|3gp|webm|wmv|flv)(\?|$)/i))) {
                                                  setMsg("⚠️ Video links are NOT allowed for Kojo Competition! Please enter a link to a Word document, PDF, TXT file, or Image.");
                                                  return;
                                                }
                                                const updated = [...batchWinners];
                                                updated[item.originalIndex].videoUrl = val;
                                                setBatchWinners(updated);
                                              }}
                                              style={{ width: "100%", padding: "6px 10px", fontSize: "12px" }}
                                            />
                                          </div>

                                          {item.videoUrl && (
                                            <div style={{ minWidth: "100px" }}>
                                              {renderAdminVideoPreview(item.videoUrl)}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                      })}
                    </div>
                  )}

                  <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                    <button
                      type="button"
                      className="admin-sc-btn admin-sc-btn-ghost"
                      onClick={handleClosePastVideoModal}
                      style={{ flex: 1, padding: 13 }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="admin-sc-btn admin-sc-btn-accent"
                      style={{ flex: 1, padding: 13 }}
                    >
                      {isEditingGroup || editingPrizeVideoId ? "✓ Update Past Video" : "📜 Save Past Competition Video"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          );
        })()}
        {/* REJECT SUBMISSION MODAL WITH REASON PROMPT */}
        {showRejectModal && (
          <div className="admin-sc-modal-overlay" style={{ zIndex: 1200 }}>
            <div
              className="admin-sc-modal accent-red"
              style={{ maxWidth: 520, padding: "28px 32px" }}
            >
              <h3
                style={{
                  marginTop: 0,
                  marginBottom: 12,
                  fontFamily: "var(--sc-font-display)",
                  fontSize: 20,
                  fontWeight: 600,
                  color: "var(--sc-red-text)",
                }}
              >
                🚫 Reject Student Submission{" "}
                <span
                  className="admin-sc-stamp"
                  style={{ color: "var(--sc-red-text)", fontSize: 14 }}
                >
                  ({rejectingSubmissionId})
                </span>
              </h3>
              <p
                style={{
                  fontSize: 14,
                  color: "var(--sc-ink-soft)",
                  marginBottom: 18,
                }}
              >
                Please specify the mandatory reason for rejecting this student
                submission. This will be recorded for audit purposes.
              </p>

              <form onSubmit={handleConfirmRejectSubmission}>
                <div style={{ marginBottom: 22 }}>
                  <label className="admin-sc-field-label">
                    Rejection Reason *
                  </label>
                  <textarea
                    className="admin-sc-textarea"
                    required
                    rows="4"
                    value={rejectionReasonInput}
                    onChange={(e) => setRejectionReasonInput(e.target.value)}
                    placeholder="Enter clear rejection reason (e.g. Invalid Video URL, Off-topic content, Incorrect School code...)"
                    style={{ width: "100%", borderRadius: 10 }}
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    type="button"
                    className="admin-sc-btn admin-sc-btn-ghost"
                    onClick={() => {
                      setShowRejectModal(false);
                      setRejectingSubmissionId("");
                      setRejectionReasonInput("");
                    }}
                    style={{ padding: "10px 20px" }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="admin-sc-btn admin-sc-btn-danger solid"
                    style={{ padding: "10px 24px", borderRadius: 8 }}
                  >
                    Confirm Rejection
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        {/* MANAGE CATEGORIES MODAL */}
        {isManageCategoriesModalOpen && (
          <div className="admin-sc-modal-overlay" style={{ zIndex: 1100 }}>
            <div className="admin-sc-modal" style={{ maxWidth: 640, width: "90%", padding: "28px 32px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <h3 style={{ margin: 0, fontFamily: "var(--sc-font-display)", fontSize: 22, fontWeight: 600, color: "var(--sc-navy)" }}>
                  🏷️ Manage Competition Categories
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    setIsManageCategoriesModalOpen(false);
                    setEditingCategoryIndex(null);
                    setEditingCategoryValue("");
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: 24,
                    color: "var(--sc-slate-soft)",
                    cursor: "pointer",
                    padding: 0,
                    lineHeight: 1
                  }}
                >
                  &times;
                </button>
              </div>

              <p style={{ fontSize: "14px", color: "var(--sc-slate)", marginBottom: 22 }}>
                You can edit (rename) or delete existing categories. Editing a category will update all active competitions under that category. Deleting a category will reset those competitions to "General".
              </p>

              <div style={{ maxHeight: "320px", overflowY: "auto", border: "1px solid var(--sc-border)", borderRadius: "8px", marginBottom: "22px" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#f8fafc", borderBottom: "1px solid var(--sc-border)" }}>
                      <th style={{ textAlign: "left", padding: "10px 14px", fontSize: "13px", fontWeight: "600", color: "#64748b" }}>Category Name</th>
                      <th style={{ textAlign: "right", padding: "10px 14px", fontSize: "13px", fontWeight: "600", color: "#64748b", width: "160px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map((cat, idx) => (
                      <tr key={idx} style={{ borderBottom: "1px solid var(--sc-border)", transition: "background-color 0.2s" }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#fdfdfd"} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}>
                        <td style={{ padding: "12px 14px", fontSize: "14px", fontWeight: "600", color: "var(--sc-ink)" }}>
                          {editingCategoryIndex === idx ? (
                            <input
                              type="text"
                              className="admin-sc-input"
                              style={{ width: "100%", padding: "6px 10px", margin: 0, fontSize: "14px", borderRadius: "6px" }}
                              value={editingCategoryValue}
                              onChange={(e) => setEditingCategoryValue(e.target.value)}
                            />
                          ) : (
                            cat
                          )}
                        </td>
                        <td style={{ padding: "12px 14px", textAlign: "right" }}>
                          {editingCategoryIndex === idx ? (
                            <div style={{ display: "flex", gap: "6px", justifyContent: "flex-end" }}>
                              <button
                                type="button"
                                className="admin-sc-btn admin-sc-btn-accent solid"
                                style={{ padding: "5px 12px", fontSize: "12px", borderRadius: "6px" }}
                                onClick={() => handleRenameCategory(cat, editingCategoryValue)}
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                className="admin-sc-btn admin-sc-btn-ghost"
                                style={{ padding: "5px 12px", fontSize: "12px", borderRadius: "6px" }}
                                onClick={() => {
                                  setEditingCategoryIndex(null);
                                  setEditingCategoryValue("");
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div style={{ display: "flex", gap: "6px", justifyContent: "flex-end" }}>
                              <button
                                type="button"
                                className="admin-sc-btn admin-sc-btn-warning"
                                style={{ padding: "5px 12px", fontSize: "12px", borderRadius: "6px" }}
                                onClick={() => {
                                  setEditingCategoryIndex(idx);
                                  setEditingCategoryValue(cat);
                                }}
                              >
                                ✏️ Edit
                              </button>
                              <button
                                type="button"
                                className="admin-sc-btn admin-sc-btn-danger"
                                style={{ padding: "5px 12px", fontSize: "12px", borderRadius: "6px" }}
                                onClick={() => handleDeleteCategory(cat)}
                              >
                                🗑️ Delete
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  className="admin-sc-btn admin-sc-btn-ghost"
                  style={{ padding: "10px 24px", borderRadius: "8px" }}
                  onClick={() => {
                    setIsManageCategoriesModalOpen(false);
                    setEditingCategoryIndex(null);
                    setEditingCategoryValue("");
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        {/* QUICK ADD NEW CATEGORY MODAL */}
        {showAddCategoryModal && (
          <div className="admin-sc-modal-overlay" style={{ zIndex: 1200 }}>
            <div className="admin-sc-modal" style={{ maxWidth: 450, padding: "28px 24px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "var(--sc-navy)" }}>
                  ➕ Add New Category
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddCategoryModal(false);
                    setNewCategoryModalInput("");
                  }}
                  style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#64748b" }}
                >
                  &times;
                </button>
              </div>
              <p style={{ fontSize: "13px", color: "#64748b", marginBottom: 16 }}>
                Add a new competition category. It will immediately appear in all competition dropdowns.
              </p>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const val = newCategoryModalInput.trim();
                  if (val) {
                    if (!categories.includes(val)) {
                      setCategories([...categories, val]);
                    }
                    setMsg(`✓ Category '${val}' added successfully!`);
                    setShowAddCategoryModal(false);
                    setNewCategoryModalInput("");
                  }
                }}
              >
                <div style={{ marginBottom: 18 }}>
                  <label className="admin-sc-field-label">Category Name *</label>
                  <input
                    type="text"
                    required
                    autoFocus
                    className="admin-sc-input"
                    value={newCategoryModalInput}
                    onChange={(e) => setNewCategoryModalInput(e.target.value)}
                    placeholder="e.g. Art, Quiz, Essay Writing"
                    style={{ width: "100%", marginTop: 4 }}
                  />
                </div>
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                  <button
                    type="button"
                    className="admin-sc-btn admin-sc-btn-ghost"
                    onClick={() => {
                      setShowAddCategoryModal(false);
                      setNewCategoryModalInput("");
                    }}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="admin-sc-btn admin-sc-btn-primary">
                    + Add Category
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
